import { useState } from "react";
import { Box, Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { addDays, format, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from "date-fns";
import { getItems } from "../../../api/items";
import { useFields } from "../../../api/useFields";
import {
    dailyCountsFromGroupedRows,
    dailyPointsFromCountMap,
    dayLabelWithWeekday,
    monthLabelFromIndex,
    monthlyCountsFromGroupedRows,
    monthlyPointsFromCountMaps,
    monthRangeFromIndex,
    singleCount,
    treatmentsWithFirstSessionInRange,
} from "../../../dashboard-framework/aggregateRows";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { resolveDatePreset, resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { drillDownFromAggregate } from "../../../dashboard-framework/drilldown";
import { formatEuros, formatPercent, formatQuantity } from "../../../dashboard-framework/format";
import { combineFilters } from "../../../dashboard-framework/useAggregate";
import { describeSessionsCount, formatDateRangePt } from "../../../dashboard-framework/describeWidgetLogic";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { DashboardSection } from "../../../dashboard-framework/DashboardSection";
import { DashboardDateFilter } from "../../../dashboard-framework/DashboardDateFilter";
import { useAggregate } from "../../../dashboard-framework/useAggregate";
import { useGlobalFilterValues } from "../../../dashboard-framework/useGlobalFilterValues";
import { buildPivot, PivotMeasure, PivotNode } from "../../../dashboard-framework/pivot";
import { GlobalFilterValues } from "../../../dashboard-framework/types";
import { ComboBarLineChart } from "../../../dashboard-framework/widgets/ComboBarLineChart";
import { ComposedBarLineChart } from "../../../dashboard-framework/widgets/ComposedBarLineChart";
import { GaugeCard } from "../../../dashboard-framework/widgets/GaugeCard";
import { HorizontalGaugeCard } from "../../../dashboard-framework/widgets/HorizontalGaugeCard";
import { PivotTableCard } from "../../../dashboard-framework/widgets/PivotTableCard";
import { StackedBarChart } from "../../../dashboard-framework/widgets/StackedBarChart";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { TimeSeriesChart } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { orderItemTotal } from "../prices/pricesRows";
import { CLIENT_TYPE_LABELS, clientTypeOfAge, coarseDisorderGroupLabel, coarseDisorderGroupOf } from "../shared/disorderGroups";
import { leadTypeChipsFromSessions, SessionLeadType, sessionClientType } from "../shared/leadClientTypes";
import { UpcomingEvaluationsGauges } from "../shared/UpcomingEvaluationsGauges";
import { useSessionsByLeadType } from "../shared/useSessionsByLeadType";
import { fetchAllItemPages } from "../shared/fetchAllItemPages";
import {
    formatDrillDate,
    formatDrillDateTime,
    LEADS_DRILL_FIELDS,
    leadRoute,
    leadsDrillColumns,
    SESSIONS_DRILL_FIELDS,
    sessionRoute,
    sessionsDrillColumns,
    TREATMENTS_DRILL_FIELDS,
    treatmentRoute,
    treatmentsDrillColumns,
} from "../shared/drillColumns";
import { FIRST_EEG_SERVICE, NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import {
    evaluationsCountQuery,
    EVALUATIONS_HAPPENING_STATUSES,
    TREATMENT_SERVICE_MIX_LABELS,
    treatmentServicesLabelByTreatmentId,
    treatmentsFirstSessionQuery,
    treatmentsFirstStartedSessionQuery,
    treatmentsServiceMixQuery,
} from "../shared/sharedMetrics";
import {
    AD_GROUP_DATE_PRESETS,
    AD_GROUP_DEFAULT_PRESET,
    AD_GROUP_SOURCE_ORDER,
    ADSET_STATUS_DEFAULT_PRESET,
    ADSET_STATUS_MIN_LEADS,
    CLINIC_SERIES,
    DAILY_LEADS_DATE_PRESETS,
    DAILY_LEADS_DEFAULT_PRESET,
    dailyFirstVisitBookingsQuery,
    dailyLeadsByClinicQuery,
    dailyLeadsQuery,
    EVALUATION_REVENUE_SERVICES,
    EVALUATIONS_DONE_30_DAYS_GOAL,
    EVALUATIONS_STARTED_STATUSES,
    facebookSpendByAdsetQuery,
    evaluationsBookedByClinicQuery,
    evaluationsDoneByClinicMonthlyQuery,
    evaluationsDoneMonthlyQuery,
    FIRST_VISIT_SERVICES,
    LEADS_LAST_7_DAYS_GOAL,
    leadsBookedMonthlyQuery,
    leadsByClientTypeQuery,
    leadsByClinicQuery,
    leadsCountQuery,
    leadsEvaluatedMonthlyQuery,
    leadsMonthlyQuery,
    pendingFirstEvaluationLeadsFilter,
    QUALIFIED_CATEGORIES_ORDER,
    QUALIFIED_LEAD_FILTER,
    BOOKED_LEAD_FILTER,
    SEGMENT_DATE_PRESETS,
    SEGMENT_DEFAULT_PRESET,
    spendDailyQuery,
    spendMonthlyQuery,
    spendSumQuery,
    TO_COME_STATUSES,
    TREATMENTS_NEXT_15_DAYS_TARGET,
    treatmentsStartedCountQuery,
    treatmentsStartedMonthlyQuery,
} from "./digitalMarketingConfig";
import {
    AD_GROUP_CAMPAIGN_FALLBACK,
    adGroupLabelOfLead,
    AdGroupRow,
    adsetBookingStatePoints,
    adsetCostPoints,
    adsetLabelOf,
    AdsetStatusLead,
    averageOfCounts,
    buildAdGroupRows,
    cohortKpis,
    dailyLeadsByClinicPoints,
    dailySumFromGroupedRows,
    facebookAdsetNameOfLead,
    facebookSpendByAdsetName,
    latestCreatedSession,
    LeadBookingState,
    leadBookingStateOf,
    leadTypeChips,
    leadTypeCounts,
    leadTypeFromClientType,
    monthlyCountsByClinic,
    monthlyEfficiencyPoints,
    monthlySumFromGroupedRows,
    mktCostPerGoalPoints,
    openTreatmentsByLeadCreationMonth,
    pendingEvaluationsByLeadCreationMonth,
    pointsWithMoreLeadsThan,
    qualifiedCategoryForStatus,
    salesByClinicMonthly,
    salesByLeadCreationMonth,
    sourceLabelOf,
    spendByAdsetRows,
    totalLeadsOfAdsetPoint,
    treatmentsStartedByTypePoints,
    weeklyLeadsBookingsCostsPoints,
    weeklySeriesPoints,
} from "./digitalMarketingRows";
import {
    SEGMENT_CONVERSION_STAGES,
    SEGMENT_COST_STAGES,
    SEGMENT_SESSION_LEVEL_COLUMNS,
    SegmentLeadRow,
    segmentChartPoints,
    segmentDrillRows,
    segmentFunnelPivot,
    segmentLeadRows,
} from "./digitalMarketingSegmentRows";

const CLINIC_LABELS: Record<number, string> = { 1: "Porto", 2: "Virtual", 3: "Braga" };
const GAUGE_MIDDLE_COLOR = "#F2B33D";
// Cores por clínica (como no Zoho): Porto azul, Braga vermelho, Virtual laranja.
const CLINIC_SERIES_COLORS = [DASHBOARD_COLORS.highlight, DASHBOARD_COLORS.cancelled, DASHBOARD_COLORS.orange];

// Linha de Total sobreposta às séries por categoria: escura e mais grossa, para se ler como o agregado.
// Partilhada por TODOS os gráficos com Total, para a linha ser sempre a mesma coisa em qualquer gráfico.
// NUNCA entra nos arrays de que se derivam as chaves das séries (ex.: LEAD_TYPE_KEYS) — "total" não é uma
// categoria, é a soma calculada pelos helpers dos pontos.
const TOTAL_SERIES = { key: "total", label: "Total", color: DASHBOARD_COLORS.total, strokeWidth: 3 };

// Gráfico "Leads qualificadas por dia": Total (linha grossa) + séries por clínica (Porto azul, Braga vermelho).
const DAILY_LEADS_CLINICS = [
    { id: 1, key: "porto" },
    { id: 3, key: "braga" },
];
const DAILY_LEADS_BY_CLINIC_SERIES = [
    TOTAL_SERIES,
    { key: "porto", label: "Porto", color: DASHBOARD_COLORS.highlight },
    { key: "braga", label: "Braga", color: DASHBOARD_COLORS.cancelled },
];

const LEAD_TYPE_SERIES = [
    { key: "Adulto", label: "Adulto", color: DASHBOARD_COLORS.highlight },
    { key: "Criança", label: "Criança", color: DASHBOARD_COLORS.done },
    { key: "Desconhecido", label: "Desconhecido", color: DASHBOARD_COLORS.neutral },
];
const LEAD_TYPE_KEYS = LEAD_TYPE_SERIES.map((serie) => serie.key);
const LEAD_TYPE_SERIES_WITH_TOTAL = [TOTAL_SERIES, ...LEAD_TYPE_SERIES];

const EEG_BY_CLINIC_SERIES_WITH_TOTAL = [
    TOTAL_SERIES,
    { key: "Porto", label: "Porto", color: DASHBOARD_COLORS.highlight },
    { key: "Braga", label: "Braga", color: DASHBOARD_COLORS.cancelled },
];

// Comparação crianças vs adultos: as mesmas cores dos gráficos "por tipo de lead" (Criança verde,
// Adulto azul) para a leitura ser imediata entre secções. "Desconhecido" fica fora dos gráficos — é o
// balde das leads nunca qualificadas, mostrado só na tabela.
const SEGMENT_COMPARISON_SERIES = [
    { key: "Criança", label: "Criança", color: DASHBOARD_COLORS.done },
    { key: "Adulto", label: "Adulto", color: DASHBOARD_COLORS.highlight },
];
const SEGMENT_LEADS_MAX_PAGES = 12;

// Estado da última marcação das leads Facebook: realizada encostada ao eixo, para comparar grupos de anúncios pelo que importa.
const ADSET_STATUS_SERIES: Array<{ key: LeadBookingState; label: string; color: string }> = [
    { key: "done", label: "Realizada", color: DASHBOARD_COLORS.done },
    { key: "pending", label: "Marcada, por realizar", color: DASHBOARD_COLORS.toCome },
    { key: "cancelled", label: "Cancelada/faltou, sem remarcação", color: DASHBOARD_COLORS.cancelled },
    { key: "notBooked", label: "Sem marcação", color: DASHBOARD_COLORS.neutral },
];
const ADSET_COST_SERIES = [
    { key: "costPerQualifiedLead", label: "Custo/lead potencial", color: DASHBOARD_COLORS.highlight },
    { key: "costPerBooking", label: "Custo/marcação", color: DASHBOARD_COLORS.toCome },
    { key: "costPerDone", label: "Custo/marcação realizada", color: DASHBOARD_COLORS.done },
];
const ADSET_STATUS_LEADS_MAX_PAGES = 6;
// Os dois gráficos lado a lado têm a mesma altura por grupo de anúncios: as linhas leem-se de um para o outro.
const ADSET_CHART_ROW_HEIGHT = 48;
const ADSET_CHART_CHROME_HEIGHT = 80;
const bookingStateLabelOf = (state: LeadBookingState) => ADSET_STATUS_SERIES.find((serie) => serie.key === state)?.label ?? state;

const TREATMENT_TYPE_SERIES = [
    TOTAL_SERIES,
    { key: "nfbOnly", label: TREATMENT_SERVICE_MIX_LABELS.nfbOnly, color: DASHBOARD_COLORS.done },
    { key: "psyOnly", label: TREATMENT_SERVICE_MIX_LABELS.psyOnly, color: DASHBOARD_COLORS.orange },
    { key: "both", label: TREATMENT_SERVICE_MIX_LABELS.both, color: DASHBOARD_COLORS.highlight },
];

const sumBy = <ItemType,>(items: Array<ItemType>, valueOf: (item: ItemType) => number) => items.reduce((total, item) => total + valueOf(item), 0);

// Chips from aggregate rows grouped by client_type (lead cards); session cards use the shared
// leadTypeChipsFromSessions.
const leadTypeChipsFromGroupedRows = (rows: Array<{ client_type: string | null; count: string | number }>) =>
    leadTypeChips(leadTypeCounts(rows.map((row) => ({ clientType: row.client_type, count: Number(row.count) }))));
const doneOverScheduledChip = (label: string, doneCount: number, scheduledCount: number) => `${label}: ${doneCount}/${scheduledCount}`;
const leadTypeCountsFromSessions = (sessions: Array<SessionLeadType>) =>
    leadTypeCounts(sessions.map((session) => ({ clientType: sessionClientType(session), count: 1 })));
const doneOverScheduledTypeChips = (doneSessions: Array<SessionLeadType>, scheduledSessions: Array<SessionLeadType>) => {
    const doneCounts = leadTypeCountsFromSessions(doneSessions);
    const scheduledCounts = leadTypeCountsFromSessions(scheduledSessions);
    const chips = [doneOverScheduledChip("Crianças", doneCounts.child, scheduledCounts.child), doneOverScheduledChip("Adultos", doneCounts.adult, scheduledCounts.adult)];
    if (scheduledCounts.unknown > 0) chips.push(doneOverScheduledChip("Sem tipo", doneCounts.unknown, scheduledCounts.unknown));
    return chips;
};
const euroOrBlank = (value: number | null) => (value === null || value === 0 ? "" : formatEuros(value));
const sessionCreatedByName = (session: any) => {
    const user = session.user_created;
    if (!user) return null;
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    return name || null;
};

export const DigitalMarketingDashboard = () => {
    const { values, setValue } = useGlobalFilterValues([]);

    // Filtros de data próprios (widgets com controlo local, sem filtro global no dashboard).
    const [adGroupFilterValues, setAdGroupFilterValues] = useState<GlobalFilterValues>({ adGroupPeriod: AD_GROUP_DEFAULT_PRESET });
    const [adsetStatusFilterValues, setAdsetStatusFilterValues] = useState<GlobalFilterValues>({ adsetStatusPeriod: ADSET_STATUS_DEFAULT_PRESET });
    const [clientTypeFilterValues, setClientTypeFilterValues] = useState<GlobalFilterValues>({ clientTypePeriod: "este_ano" });
    const [disorderFilterValues, setDisorderFilterValues] = useState<GlobalFilterValues>({ disorderPeriod: "este_ano" });
    const [dailyLeadsFilterValues, setDailyLeadsFilterValues] = useState<GlobalFilterValues>({ dailyLeadsPeriod: DAILY_LEADS_DEFAULT_PRESET });
    const [segmentFilterValues, setSegmentFilterValues] = useState<GlobalFilterValues>({ segmentPeriod: SEGMENT_DEFAULT_PRESET });
    const setLocalFilter = (setter: React.Dispatch<React.SetStateAction<GlobalFilterValues>>) => (filterKey: string, value: string | number | null) =>
        setter((previous) => ({ ...previous, [filterKey]: value }));

    const now = new Date();
    const todayRange = resolveDatePreset("hoje", now);
    const monthRange = resolveDatePreset("este_mes", now);
    const last7DaysRange = { gte: format(subDays(now, 6), "yyyy-MM-dd"), lt: todayRange.lt };
    const last30DaysRange = resolveDatePreset("ultimos_30_dias", now);
    const next15DaysRange = { gte: todayRange.gte, lt: format(addDays(now, 15), "yyyy-MM-dd") };
    const last12FullMonthsRange = {
        gte: format(startOfMonth(subMonths(now, 12)), "yyyy-MM-dd"),
        lt: format(startOfMonth(now), "yyyy-MM-dd"),
    };
    const monthlyChartFilter = { _gte: last12FullMonthsRange.gte, _lt: monthRange.lt };
    const cohortRange = {
        gte: format(startOfMonth(subMonths(now, 13)), "yyyy-MM-dd"),
        lt: format(startOfMonth(subMonths(now, 2)), "yyyy-MM-dd"),
    };
    const last13WeeksISO = format(subWeeks(now, 13), "yyyy-MM-dd");
    const last15DaysRange = { gte: format(subDays(now, 15), "yyyy-MM-dd"), lt: todayRange.lt };
    const weeklyChartRange = { gte: format(startOfWeek(subWeeks(now, 15), { weekStartsOn: 1 }), "yyyy-MM-dd"), lt: todayRange.lt };

    const adGroupRange = resolveRequiredDateFilterRange(adGroupFilterValues, "adGroupPeriod", AD_GROUP_DEFAULT_PRESET, now);
    const adsetStatusRange = resolveRequiredDateFilterRange(adsetStatusFilterValues, "adsetStatusPeriod", ADSET_STATUS_DEFAULT_PRESET, now);
    const dailyLeadsByClinicRange = resolveRequiredDateFilterRange(dailyLeadsFilterValues, "dailyLeadsPeriod", DAILY_LEADS_DEFAULT_PRESET, now);
    const clientTypeRange = resolveRequiredDateFilterRange(clientTypeFilterValues, "clientTypePeriod", "este_ano", now);
    const disorderRange = resolveRequiredDateFilterRange(disorderFilterValues, "disorderPeriod", "este_ano", now);
    const segmentRange = resolveRequiredDateFilterRange(segmentFilterValues, "segmentPeriod", SEGMENT_DEFAULT_PRESET, now);

    const leadsTodayFilter = { date_created: { _gte: todayRange.gte, _lt: todayRange.lt } };
    // Filtro PLANO (um só `_and`) para o drill não cair na armadilha do `_and` aninhado — ver README do framework.
    const qualifiedLeadsTodayFilter = { _and: [leadsTodayFilter, QUALIFIED_LEAD_FILTER] };
    const bookedLeadsTodayFilter = { _and: [leadsTodayFilter, BOOKED_LEAD_FILTER] };
    const leadsUpdatedTodayFilter = { date_updated: { _gte: todayRange.gte, _lt: todayRange.lt } };
    const evalBookedTodayFilter = { date_created: { _gte: todayRange.gte, _lt: todayRange.lt } };
    const evalDoneTodayFilter = { _and: [{ status: { _in: EVALUATIONS_STARTED_STATUSES } }, { start_date: { _gte: todayRange.gte, _lt: todayRange.lt } }] };
    const evalScheduledTodayFilter = { _and: [{ status: { _in: EVALUATIONS_HAPPENING_STATUSES } }, { start_date: { _gte: todayRange.gte, _lt: todayRange.lt } }] };
    const leadsLast7Filter = { date_created: { _gte: last7DaysRange.gte, _lt: last7DaysRange.lt } };
    const evalDone30Filter = { _and: [{ status: { _eq: "done" } }, { start_date: { _gte: last30DaysRange.gte, _lt: last30DaysRange.lt } }] };
    const evalBookedMonthFilter = { _and: [{ status: { _in: TO_COME_STATUSES } }, { start_date: { _gte: monthRange.gte, _lt: monthRange.lt } }] };
    const evalDoneMonthFilter = { _and: [{ status: { _eq: "done" } }, { start_date: { _gte: monthRange.gte, _lt: monthRange.lt } }] };
    const salesMonthFilter = { date_of_start: { _gte: monthRange.gte, _lt: monthRange.lt } };
    const untilEndOfMonthRange = { gte: todayRange.gte, lt: monthRange.lt };
    const spendTodayFilter = { date: { _gte: todayRange.gte, _lt: todayRange.lt } };

    // KPIs "hoje" + custos
    const leadsTodayResult = useAggregate(leadsCountQuery, { filter: leadsTodayFilter });
    const qualifiedLeadsTodayResult = useAggregate(leadsCountQuery, { filter: qualifiedLeadsTodayFilter });
    const bookedLeadsTodayResult = useAggregate(leadsCountQuery, { filter: bookedLeadsTodayFilter });
    const leadsUpdatedTodayResult = useAggregate(leadsCountQuery, { filter: leadsUpdatedTodayFilter });
    const evalBookedTodayResult = useAggregate(evaluationsBookedByClinicQuery, { filter: evalBookedTodayFilter });
    const evalDoneTodayResult = useAggregate(evaluationsCountQuery, { filter: evalDoneTodayFilter });
    const evalScheduledTodayResult = useAggregate(evaluationsCountQuery, { filter: evalScheduledTodayFilter });
    const spendTodayResult = useAggregate(spendSumQuery, { filter: spendTodayFilter });
    // Repartição por clínica para os chips dos cartões "hoje".
    const leadsTodayByClinicResult = useAggregate(leadsByClinicQuery, { filter: leadsTodayFilter });
    const evalDoneTodayByClinicResult = useAggregate(evaluationsBookedByClinicQuery, { filter: evalDoneTodayFilter });
    const evalScheduledTodayByClinicResult = useAggregate(evaluationsBookedByClinicQuery, { filter: evalScheduledTodayFilter });
    // Repartição crianças/adultos: leads por aggregate (client_type é coluna da leads), sessões por itens
    // crus com lead.client_type (o groupBy relacional dá 500 no Directus). Vigia a segmentação das campanhas.
    const leadsTodayByTypeResult = useAggregate(leadsByClientTypeQuery, { filter: leadsTodayFilter });
    const leadsLast7ByTypeResult = useAggregate(leadsByClientTypeQuery, { filter: leadsLast7Filter });
    const evalBookedTodayByTypeResult = useSessionsByLeadType(["eval-booked-today", todayRange.gte], { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, evalBookedTodayFilter] });
    const evalDoneTodayByTypeResult = useSessionsByLeadType(["eval-done-today", todayRange.gte], { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, evalDoneTodayFilter] });
    const evalScheduledTodayByTypeResult = useSessionsByLeadType(["eval-scheduled-today", todayRange.gte], { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, evalScheduledTodayFilter] });
    const evalDone30ByTypeResult = useSessionsByLeadType(["eval-done-30", last30DaysRange.gte], { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, evalDone30Filter] });
    // Definições dos campos das leads → chips com cor/label do Directus nos drill-downs (client_type, status).
    const { formatedFields: leadFields } = useFields("leads");

    // Gauges
    const leadsLast7Result = useAggregate(leadsCountQuery, { filter: leadsLast7Filter });
    const evalDone30Result = useAggregate(evaluationsCountQuery, { filter: evalDone30Filter });

    // KPIs mês / tratamentos a começar
    const treatmentsFirstSessionResult = useAggregate(treatmentsFirstSessionQuery);
    const upcomingNext15 = treatmentsWithFirstSessionInRange(treatmentsFirstSessionResult.data || [], next15DaysRange);
    const upcomingUntilEndOfMonth = treatmentsWithFirstSessionInRange(treatmentsFirstSessionResult.data || [], untilEndOfMonthRange);
    const treatmentsNext15 = upcomingNext15.length;
    const treatmentsUntilEndOfMonth = upcomingUntilEndOfMonth.length;
    const evalBookedThisMonthResult = useAggregate(evaluationsCountQuery, { filter: evalBookedMonthFilter });
    const evalDoneThisMonthResult = useAggregate(evaluationsCountQuery, { filter: evalDoneMonthFilter });
    const salesThisMonthResult = useAggregate(treatmentsStartedCountQuery, { filter: salesMonthFilter });

    const upcomingUnionIds = [...new Set([...upcomingNext15, ...upcomingUntilEndOfMonth].map((treatment) => treatment.treatmentId))].slice(0, 800);
    const upcomingNamesResult = useQuery({
        queryKey: ["mkt-upcoming-treatment-names", todayRange.gte, upcomingUnionIds.length, upcomingUnionIds[0]],
        queryFn: () =>
            getItems("treatments", "id,client.user_id.first_name,client.user_id.last_name", {
                filter: { id: { _in: upcomingUnionIds } },
                pageSize: 1000,
                sort: "id",
            }),
        enabled: upcomingUnionIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });
    const upcomingClientNamesById = new Map<number | string, string>(
        (upcomingNamesResult.data?.items || []).map((treatment: any) => {
            const user = treatment.client?.user_id;
            const clientName = user ? [user.first_name, user.last_name].filter(Boolean).join(" ").trim() : "";
            return [treatment.id, clientName || `Tratamento #${treatment.id}`];
        })
    );
    const upcomingDrillRowsFor = (upcoming: Array<{ treatmentId: number | string; firstSessionDate: string }>) =>
        upcoming.map((treatment) => ({
            id: treatment.treatmentId,
            clientName: upcomingClientNamesById.get(Number(treatment.treatmentId)) || `Tratamento #${treatment.treatmentId}`,
            firstSessionDate: treatment.firstSessionDate,
        }));

    // Séries mensais (13 meses)
    const evalDoneMonthlyResult = useAggregate(evaluationsDoneMonthlyQuery, { filter: { start_date: monthlyChartFilter } });
    const salesMonthlyResult = useAggregate(treatmentsStartedMonthlyQuery, { filter: { date_of_start: monthlyChartFilter } });
    const spendMonthlyResult = useAggregate(spendMonthlyQuery, { filter: { date: monthlyChartFilter } });
    const leadsMonthlyResult = useAggregate(leadsMonthlyQuery, { filter: { date_created: monthlyChartFilter } });
    // Denominador do custo por lead do "Mkt Cost Per Goal": só as leads qualificadas. Query separada do
    // leadsMonthlyResult, que continua a contar TODAS as leads para o "Resultados de marketing"/eficiência.
    const qualifiedLeadsMonthlyResult = useAggregate(leadsMonthlyQuery, { filter: { _and: [{ date_created: monthlyChartFilter }, QUALIFIED_LEAD_FILTER] } });
    const leadsBookedMonthlyResult = useAggregate(leadsBookedMonthlyQuery, { filter: { date_created: monthlyChartFilter } });
    const leadsEvaluatedMonthlyResult = useAggregate(leadsEvaluatedMonthlyQuery, { filter: { date_created: monthlyChartFilter } });
    const evalDoneByClinicMonthlyResult = useAggregate(evaluationsDoneByClinicMonthlyQuery, { filter: { start_date: monthlyChartFilter } });

    const cohortLeadsResult = useAggregate(leadsMonthlyQuery, { filter: { date_created: { _gte: cohortRange.gte, _lt: cohortRange.lt } } });

    // Séries semanais (Leads-Marcações-Custos)
    const weeklyLeadsResult = useAggregate(dailyLeadsQuery, { filter: { date_created: { _gte: weeklyChartRange.gte, _lt: weeklyChartRange.lt } } });
    const weeklyBookingsResult = useAggregate(dailyFirstVisitBookingsQuery, { filter: { date_created: { _gte: weeklyChartRange.gte, _lt: weeklyChartRange.lt } } });
    const weeklySpendResult = useAggregate(spendDailyQuery, { filter: { date: { _gte: weeklyChartRange.gte, _lt: weeklyChartRange.lt } } });

    // Charts diários (últimos 15 dias)
    const dailyLeadsResult = useAggregate(dailyLeadsQuery, { filter: { date_created: { _gte: last15DaysRange.gte, _lt: last15DaysRange.lt } } });
    const dailyLeadsByClinicResult = useAggregate(dailyLeadsByClinicQuery, { filter: { _and: [{ date_created: { _gte: dailyLeadsByClinicRange.gte, _lt: dailyLeadsByClinicRange.lt } }, QUALIFIED_LEAD_FILTER] } });
    const dailyBookingsResult = useAggregate(dailyFirstVisitBookingsQuery, { filter: { date_created: { _gte: last15DaysRange.gte, _lt: last15DaysRange.lt } } });

    const cohortTreatmentsResult = useQuery({
        queryKey: ["mkt-cohort-treatments", cohortRange.gte, cohortRange.lt],
        queryFn: () =>
            getItems("treatments", "id,date_of_start,client.lead.date_created", {
                filter: { client: { lead: { date_created: { _gte: cohortRange.gte, _lt: cohortRange.lt } } } },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const efficiencyTreatmentsResult = useQuery({
        queryKey: ["mkt-efficiency-treatments", last12FullMonthsRange.gte],
        queryFn: () =>
            getItems("treatments", "id,date_of_start,treatment_status_new,client.lead.date_created", {
                filter: { client: { lead: { date_created: { _gte: last12FullMonthsRange.gte, _lt: monthRange.lt } } } },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const pendingEvaluationLeadsResult = useQuery({
        queryKey: ["mkt-pending-evaluation-leads", last12FullMonthsRange.gte, todayRange.gte],
        queryFn: () =>
            getItems("leads", "id,date_created,progress", {
                filter: { _and: [{ date_created: monthlyChartFilter }, pendingFirstEvaluationLeadsFilter(todayRange.gte)] },
                pageSize: 1000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const salesByClinicResult = useQuery({
        queryKey: ["mkt-sales-by-clinic", last12FullMonthsRange.gte],
        queryFn: () =>
            getItems("treatments", "id,date_of_start,sessions.service,sessions.clinic_id,sessions.start_date", {
                filter: { date_of_start: monthlyChartFilter },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const recentLeadsResult = useQuery({
        queryKey: ["mkt-recent-leads", last13WeeksISO],
        queryFn: () =>
            getItems("leads", "id,date_created,status,client_type", {
                filter: { date_created: { _gte: last13WeeksISO } },
                pageSize: 4000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const recentBookedEegResult = useQuery({
        queryKey: ["mkt-recent-booked-eeg", last13WeeksISO],
        queryFn: () =>
            getItems("sessions", "id,date_created,clinic_id,lead.client_type", {
                filter: { _and: [{ service: { _eq: 39 } }, { date_created: { _gte: last13WeeksISO } }] },
                pageSize: 4000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const recentDoneEegResult = useQuery({
        queryKey: ["mkt-recent-done-eeg", last13WeeksISO],
        queryFn: () =>
            getItems("sessions", "id,start_date,lead.client_type", {
                filter: { _and: [{ service: { _eq: 39 } }, { status: { _eq: "done" } }, { start_date: { _gte: last13WeeksISO } }] },
                pageSize: 4000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const revenueItemsResult = useQuery({
        queryKey: ["mkt-evaluation-revenue", last12FullMonthsRange.gte],
        queryFn: () =>
            getItems("order_items", "id,price,quantity,discount,order_id.id,order_id.order_date", {
                filter: {
                    _and: [
                        { service_variant: { service: { _in: EVALUATION_REVENUE_SERVICES } } },
                        { order_id: { order_date: { _gte: last12FullMonthsRange.gte } } },
                    ],
                },
                pageSize: 4000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });

    // Tabela de grupos de anúncios (filtro de datas próprio). Facebook por grupo de anúncios (lead.utm_term e
    // spend.adset), Google e sem tracking por campanha. A chave inclui "adset" para não reaproveitar a cache
    // persistida das versões sem utm_term/adset.
    const adGroupLeadsResult = useQuery({
        queryKey: ["mkt-adgroup-leads", "adset", adGroupRange.gte, adGroupRange.lt],
        queryFn: () =>
            getItems("leads", "id,utm_source,utm_campaign,utm_term,progress,status", {
                filter: { date_created: { _gte: adGroupRange.gte, _lt: adGroupRange.lt } },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const adGroupSpendResult = useQuery({
        queryKey: ["mkt-adgroup-spend", "adset", adGroupRange.gte, adGroupRange.lt],
        queryFn: () =>
            getItems("marketing_spend", "id,amount_spent,platform,ad.utm_campaign,adset.name", {
                filter: { date: { _gte: adGroupRange.gte, _lt: adGroupRange.lt } },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });

    // Estado das leads Facebook por grupo de anúncios (filtro de datas próprio): as leads do período com TODAS as
    // sessões ligadas. O utm_term da lead é o id do grupo de anúncios no Facebook; o nome vem de marketing_adset.
    const adsetStatusLeadsResult = useQuery({
        queryKey: ["mkt-adset-status-leads", adsetStatusRange.gte, adsetStatusRange.lt],
        queryFn: () =>
            fetchAllItemPages(
                "leads",
                "id,first_name,last_name,date_created,status,utm_campaign,utm_term,sessions.id,sessions.status,sessions.date_created,sessions.start_date",
                { _and: [{ utm_source: { _eq: "facebook" } }, { date_created: { _gte: adsetStatusRange.gte, _lt: adsetStatusRange.lt } }] },
                { maxPages: ADSET_STATUS_LEADS_MAX_PAGES }
            ),
        staleTime: 5 * 60 * 1000,
    });
    const facebookAdsetNamesResult = useQuery({
        queryKey: ["mkt-facebook-adset-names"],
        queryFn: () => getItems("marketing_adset", "id,adset_id,name", { filter: { platform: { _eq: "facebook" } }, pageSize: 5000, sort: "id" }),
        staleTime: 5 * 60 * 1000,
    });
    const facebookSpendByAdsetResult = useAggregate(facebookSpendByAdsetQuery, { filter: { date: { _gte: adsetStatusRange.gte, _lt: adsetStatusRange.lt } } });

    // Linhas de gasto de hoje com o grupo de anúncios (adset) — origem do drill do cartão "Custo total de marketing".
    const spendTodayRowsResult = useQuery({
        queryKey: ["mkt-spend-today-rows", todayRange.gte, todayRange.lt],
        queryFn: () =>
            getItems("marketing_spend", "id,amount_spent,platform,adset.id,adset.name", {
                filter: spendTodayFilter,
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });

    // Comparação crianças vs adultos: UM único universo (leads do período + sessões de 1ª visita + tratamentos
    // do cliente) alimenta a tabela, os dois gráficos e todos os drills — assim as métricas ao nível da lead e
    // ao nível da sessão reconciliam entre si por construção. Paginado: 12 meses de leads já passa dos 4.000.
    const segmentLeadsResult = useQuery({
        queryKey: ["mkt-segment-leads", segmentRange.gte, segmentRange.lt],
        queryFn: () =>
            fetchAllItemPages(
                "leads",
                "id,first_name,last_name,date_created,status,client_type,sessions.service,sessions.status,client.treatments.date_of_start",
                { date_created: { _gte: segmentRange.gte, _lt: segmentRange.lt } },
                { maxPages: SEGMENT_LEADS_MAX_PAGES }
            ),
        staleTime: 5 * 60 * 1000,
    });
    const segmentSpendResult = useAggregate(spendSumQuery, { filter: { date: { _gte: segmentRange.gte, _lt: segmentRange.lt } } });

    // Pivots (filtros de data próprios)
    const clientTypeTreatmentsResult = useQuery({
        queryKey: ["mkt-clienttype-treatments", clientTypeRange.gte, clientTypeRange.lt],
        queryFn: () =>
            getItems("treatments", "id,date_of_start,age_when_started", {
                filter: { date_of_start: { _gte: clientTypeRange.gte, _lt: clientTypeRange.lt } },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const disorderTreatmentsResult = useQuery({
        queryKey: ["mkt-disorder-treatments", disorderRange.gte, disorderRange.lt],
        queryFn: () =>
            getItems("treatments", "id,date_of_start,diagnose_main", {
                filter: { date_of_start: { _gte: disorderRange.gte, _lt: disorderRange.lt } },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });

    // ── Derivações ──
    const bookedByClinicRows: Array<{ clinic_id: number | null; count: string | number }> = evalBookedTodayResult.data || [];
    const evalBookedTodayTotal = bookedByClinicRows.reduce((sum, row) => sum + Number(row.count), 0);
    const bookedPorto = Number(bookedByClinicRows.find((row) => Number(row.clinic_id) === 1)?.count || 0);
    const bookedBraga = Number(bookedByClinicRows.find((row) => Number(row.clinic_id) === 3)?.count || 0);
    const leadsTodayByClinicRows = leadsTodayByClinicResult.data || [];
    const leadsTodayPorto = Number(leadsTodayByClinicRows.find((row: any) => Number(row.clinic) === 1)?.count || 0);
    const leadsTodayBraga = Number(leadsTodayByClinicRows.find((row: any) => Number(row.clinic) === 3)?.count || 0);
    const leadsTodayByClinicTotal = leadsTodayByClinicRows.reduce((sum: number, row: any) => sum + Number(row.count), 0);
    const leadsTodayUnknown = leadsTodayByClinicTotal - leadsTodayPorto - leadsTodayBraga;
    const doneByClinicRows = evalDoneTodayByClinicResult.data || [];
    const doneTodayPorto = Number(doneByClinicRows.find((row: any) => Number(row.clinic_id) === 1)?.count || 0);
    const doneTodayBraga = Number(doneByClinicRows.find((row: any) => Number(row.clinic_id) === 3)?.count || 0);
    const scheduledByClinicRows = evalScheduledTodayByClinicResult.data || [];
    const scheduledTodayPorto = Number(scheduledByClinicRows.find((row: any) => Number(row.clinic_id) === 1)?.count || 0);
    const scheduledTodayBraga = Number(scheduledByClinicRows.find((row: any) => Number(row.clinic_id) === 3)?.count || 0);
    // Duas LINHAS de chips nos cartões "hoje": clínica em cima, tipo de cliente em baixo (vigilância da
    // segmentação das campanhas). Juntas numa linha só, as duas repartições leem-se como uma e confundem.
    const leadsTodayChips = [`Porto: ${leadsTodayPorto}`, `Braga: ${leadsTodayBraga}`, `Desconhecido: ${leadsTodayUnknown}`];
    const leadsTodayTypeChips = leadTypeChipsFromGroupedRows(leadsTodayByTypeResult.data || []);
    const evalBookedTodayChips = [`Porto: ${bookedPorto}`, `Braga: ${bookedBraga}`];
    const evalBookedTodayTypeChips = leadTypeChipsFromSessions(evalBookedTodayByTypeResult.data?.items || []);
    const evalDoneOverScheduledTodayValue =
        evalDoneTodayResult.data && evalScheduledTodayResult.data ? `${singleCount(evalDoneTodayResult.data)}/${singleCount(evalScheduledTodayResult.data)}` : undefined;
    const evalDoneOverScheduledTodayChips = [doneOverScheduledChip("Porto", doneTodayPorto, scheduledTodayPorto), doneOverScheduledChip("Braga", doneTodayBraga, scheduledTodayBraga)];
    const evalDoneOverScheduledTodayTypeChips = doneOverScheduledTypeChips(evalDoneTodayByTypeResult.data?.items || [], evalScheduledTodayByTypeResult.data?.items || []);
    const leadsLast7Chips = leadTypeChipsFromGroupedRows(leadsLast7ByTypeResult.data || []);
    const evalDone30Chips = leadTypeChipsFromSessions(evalDone30ByTypeResult.data?.items || []);

    const totalCostToday = Number(spendTodayResult.data?.[0]?.sum?.amount_spent || 0);
    const leadsTodayCount = leadsTodayResult.data ? singleCount(leadsTodayResult.data) : undefined;
    const totalCostTodayValue = spendTodayResult.data ? formatEuros(totalCostToday) : undefined;

    // Drill do custo de hoje: gasto por grupo de anúncios (adset), agrupado por plataforma e ordenado por gasto.
    const adsetSpendRows = spendByAdsetRows(spendTodayRowsResult.data?.items || []);
    const facebookSpendToday = sumBy(adsetSpendRows.filter((row) => row.platform === "Facebook"), (row) => row.amount_spent);
    const googleSpendToday = sumBy(adsetSpendRows.filter((row) => row.platform === "Google"), (row) => row.amount_spent);
    // Chips por plataforma no cartão "Custo total de marketing" (mesma lógica dos chips por clínica de outros cartões).
    const spendTodayChips = [`Facebook: ${formatEuros(facebookSpendToday)}`, `Google: ${formatEuros(googleSpendToday)}`];
    const spendDrillHeaderChips = [
        `Gasto total: ${formatEuros(totalCostToday)}`,
        ...(facebookSpendToday > 0 ? [`Facebook: ${formatEuros(facebookSpendToday)}`] : []),
        ...(googleSpendToday > 0 ? [`Google: ${formatEuros(googleSpendToday)}`] : []),
    ];
    const qualifiedLeadsTodayCount = qualifiedLeadsTodayResult.data ? singleCount(qualifiedLeadsTodayResult.data) : undefined;
    const costPerLeadTodayValue =
        spendTodayResult.data && qualifiedLeadsTodayResult.data
            ? qualifiedLeadsTodayCount && qualifiedLeadsTodayCount > 0
                ? formatEuros(totalCostToday / qualifiedLeadsTodayCount)
                : "–"
            : undefined;
    const costPerLeadTodayChips = [`Leads qualif.: ${formatQuantity(qualifiedLeadsTodayCount || 0)}`, `Leads totais: ${formatQuantity(leadsTodayCount || 0)}`];
    // Custo por lead marcada: as MESMAS leads do cartão "Leads hoje", só as que já têm marcação.
    const bookedLeadsTodayCount = bookedLeadsTodayResult.data ? singleCount(bookedLeadsTodayResult.data) : undefined;
    const costPerBookedLeadTodayValue =
        spendTodayResult.data && bookedLeadsTodayResult.data
            ? bookedLeadsTodayCount && bookedLeadsTodayCount > 0
                ? formatEuros(totalCostToday / bookedLeadsTodayCount)
                : "–"
            : undefined;
    const costPerBookedLeadTodayChips = [`Leads marcadas: ${formatQuantity(bookedLeadsTodayCount || 0)}`, `Leads totais: ${formatQuantity(leadsTodayCount || 0)}`];

    const nowMonthIndex = now.getFullYear() * 12 + now.getMonth();
    const firstChartMonthIndex = nowMonthIndex - 12;
    const cohortFirstMonth = nowMonthIndex - 13;
    const cohortLastMonth = nowMonthIndex - 3;
    const cohortLeadsCounts = monthlyCountsFromGroupedRows(cohortLeadsResult.data || [], "date_created");
    const cohort = cohortKpis(cohortLeadsCounts, cohortTreatmentsResult.data?.items || [], cohortFirstMonth, cohortLastMonth);
    const cohortAverageLeads = averageOfCounts(cohortLeadsCounts, cohortFirstMonth, cohortLastMonth);

    const evalMonthlyCounts = monthlyCountsFromGroupedRows(evalDoneMonthlyResult.data || [], "start_date");
    const salesMonthlyCounts = monthlyCountsFromGroupedRows(salesMonthlyResult.data || [], "date_of_start");
    const leadsMonthlyCounts = monthlyCountsFromGroupedRows(leadsMonthlyResult.data || [], "date_created");
    const qualifiedLeadsMonthlyCounts = monthlyCountsFromGroupedRows(qualifiedLeadsMonthlyResult.data || [], "date_created");
    const leadsBookedCounts = monthlyCountsFromGroupedRows(leadsBookedMonthlyResult.data || [], "date_created");
    const leadsEvaluatedCounts = monthlyCountsFromGroupedRows(leadsEvaluatedMonthlyResult.data || [], "date_created");
    const salesByLeadCohortCounts = salesByLeadCreationMonth(efficiencyTreatmentsResult.data?.items || []);
    const pendingEvaluationsCounts = pendingEvaluationsByLeadCreationMonth(pendingEvaluationLeadsResult.data?.items || []);
    const openTreatmentsCounts = openTreatmentsByLeadCreationMonth(efficiencyTreatmentsResult.data?.items || []);
    const spendMonthlyCounts = monthlySumFromGroupedRows(spendMonthlyResult.data || [], "date", "amount_spent");
    const evalMonthlyAverage = averageOfCounts(evalMonthlyCounts, nowMonthIndex - 12, nowMonthIndex);
    const salesMonthlyAverage = averageOfCounts(salesMonthlyCounts, nowMonthIndex - 12, nowMonthIndex);

    // Charts mensais por clínica (Number Of Evaluations Done / Number of sales)
    const clinicIds = CLINIC_SERIES.map((clinic) => clinic.id);
    const clinicChartSeries = CLINIC_SERIES.map((clinic, index) => ({ key: clinic.key, label: clinic.label, color: CLINIC_SERIES_COLORS[index] }));
    const evalByClinicCounts = monthlyCountsByClinic(evalDoneByClinicMonthlyResult.data || [], "start_date", clinicIds);
    const salesByClinicCounts = salesByClinicMonthly(salesByClinicResult.data?.items || [], clinicIds);
    // Pontos com monthIndex (para drill por barra) ao longo dos 13 meses fixos.
    const buildClinicPoints = (countsByClinic: Map<number, Map<number, number>>) => {
        const points: Array<Record<string, string | number>> = [];
        for (let monthIndex = firstChartMonthIndex; monthIndex <= nowMonthIndex; monthIndex++) {
            const point: Record<string, string | number> = { monthLabel: monthLabelFromIndex(monthIndex), monthIndex };
            CLINIC_SERIES.forEach((clinic) => {
                point[clinic.key] = countsByClinic.get(clinic.id)?.get(monthIndex) || 0;
            });
            points.push(point);
        }
        return points;
    };
    const evalByClinicPoints = buildClinicPoints(evalByClinicCounts);
    const salesByClinicPoints = buildClinicPoints(salesByClinicCounts);

    // Tratamentos iniciados por tipo: 1ª sessão NFB/Psi JÁ iniciada (realizada/à espera na clínica/em
    // sessão — mesma definição do cartão "Tratamentos Iniciados" da Análise Diária) no mês, na janela de
    // 13 meses dos outros gráficos mensais, classificados pelo mix de serviços do tratamento. O filtro do
    // mix por start_date basta: todas as sessões elegíveis de um tratamento são posteriores à sua 1ª
    // sessão, logo os tratamentos com início dentro da janela ficam com o mix completo.
    const chartWindowRange = { gte: monthRangeFromIndex(firstChartMonthIndex).gte, lt: monthRangeFromIndex(nowMonthIndex).lt };
    const treatmentsFirstStartedResult = useAggregate(treatmentsFirstStartedSessionQuery);
    const treatmentTypeMixResult = useAggregate(treatmentsServiceMixQuery, { filter: { start_date: { _gte: chartWindowRange.gte } } });
    const treatmentTypeLabelByTreatmentId = treatmentServicesLabelByTreatmentId(treatmentTypeMixResult.data || [], NFB_SERVICE, PSY_SERVICE);
    const treatmentsInChartWindow = treatmentsWithFirstSessionInRange(treatmentsFirstStartedResult.data || [], chartWindowRange);
    const treatmentsByTypePoints = treatmentsStartedByTypePoints(treatmentsInChartWindow, treatmentTypeLabelByTreatmentId, firstChartMonthIndex, nowMonthIndex);

    // MKT Results All + Marketing Efficiency + Mkt Cost Per Goal
    // MKT Results All = cohort pela data de CRIAÇÃO da lead (réplica Zoho, validado vs query_data):
    // leads criadas, marcações (progress booked+done), avaliações feitas (progress done), vendas (tratamentos
    // com date_of_start dessas leads). NÃO usa datas naturais (era esse o bug).
    const mktResultsPoints = monthlyPointsFromCountMaps([
        { key: "leads", countsByMonthIndex: leadsMonthlyCounts },
        { key: "bookedEvals", countsByMonthIndex: leadsBookedCounts },
        { key: "doneEvals", countsByMonthIndex: leadsEvaluatedCounts },
        { key: "sales", countsByMonthIndex: salesByLeadCohortCounts },
    ]);
    const efficiencyPoints = monthlyEfficiencyPoints(
        leadsMonthlyCounts,
        leadsBookedCounts,
        efficiencyTreatmentsResult.data?.items || [],
        firstChartMonthIndex,
        nowMonthIndex
    );
    // Denominadores em COHORT (por mês de criação da lead), coerentes com o MKT Results All e validados vs
    // Zoho: leads criadas, marcações (booked+done), avaliações feitas (progress done), vendas (date_of_start).
    // NÃO se usa o "Treatments"=85 / "Evaluations"=111 do Zoho (contagens de registos de tratamento inflacionadas
    // — 73 dos 111 de nov são "they_declined" — e incoerentes com o próprio MKT Results All do Zoho).
    const mktCostGoalPoints = mktCostPerGoalPoints(
        spendMonthlyCounts,
        qualifiedLeadsMonthlyCounts,
        leadsEvaluatedCounts,
        salesByLeadCohortCounts,
        leadsBookedCounts,
        pendingEvaluationsCounts,
        openTreatmentsCounts,
        firstChartMonthIndex,
        nowMonthIndex
    );

    // Leads-Marcações-Custos (semanal)
    const weeklyLeadsByDay = dailyCountsFromGroupedRows(weeklyLeadsResult.data || [], "date_created");
    const weeklyBookingsByDay = dailyCountsFromGroupedRows(weeklyBookingsResult.data || [], "date_created");
    const weeklySpendByDay = dailySumFromGroupedRows(weeklySpendResult.data || [], "date", "amount_spent");
    const leadsBookingsCostsPoints = weeklyLeadsBookingsCostsPoints(weeklyLeadsByDay, weeklyBookingsByDay, weeklySpendByDay, weeklyChartRange);

    // Nomes dos grupos de anúncios do Facebook: pelo id do Facebook (utm_term das leads) e pelo registo (marketing_spend.adset).
    const facebookAdsets: Array<{ id: number; adset_id: string; name: string }> = facebookAdsetNamesResult.data?.items || [];
    const facebookAdsetNameById = new Map<string, string>(facebookAdsets.map((adset) => [String(adset.adset_id), adset.name]));
    const facebookAdsetNameByRecordId = new Map<string, string>(facebookAdsets.map((adset) => [String(adset.id), adset.name]));

    // Tabela de grupos de anúncios
    const adGroupRows = buildAdGroupRows(adGroupLeadsResult.data?.items || [], adGroupSpendResult.data?.items || [], facebookAdsetNameById);
    const adGroupMeasures: Array<PivotMeasure<AdGroupRow>> = [
        { key: "leads", label: "Leads", isNumeric: true, compute: (items) => sumBy(items, (row) => row.leads), format: (value) => formatQuantity(value || 0) },
        {
            key: "qualifiedLeads",
            label: "Leads qualif.",
            isNumeric: true,
            compute: (items) => sumBy(items, (row) => row.qualifiedLeads),
            format: (value) => formatQuantity(value || 0),
        },
        {
            key: "costPerLead",
            label: "Custo/lead qualificada",
            isNumeric: true,
            compute: (items) => {
                const cost = sumBy(items, (row) => row.cost);
                const qualifiedLeads = sumBy(items, (row) => row.qualifiedLeads);
                return cost > 0 && qualifiedLeads > 0 ? cost / qualifiedLeads : null;
            },
            format: euroOrBlank,
        },
        { key: "booked", label: "1ªs visitas marcadas", isNumeric: true, compute: (items) => sumBy(items, (row) => row.booked), format: (value) => formatQuantity(value || 0) },
        {
            key: "pctBooking",
            label: "% marcação",
            isNumeric: true,
            compute: (items) => {
                const leads = sumBy(items, (row) => row.leads);
                const booked = sumBy(items, (row) => row.booked);
                return leads > 0 ? (booked / leads) * 100 : null;
            },
            format: (value) => (value === null ? "" : formatPercent(value)),
        },
        { key: "cost", label: "Custos mkt", isNumeric: true, compute: (items) => sumBy(items, (row) => row.cost), format: euroOrBlank },
        {
            key: "costPerBooked",
            label: "Custo/1ª visita marcada",
            isNumeric: true,
            compute: (items) => {
                const cost = sumBy(items, (row) => row.cost);
                const booked = sumBy(items, (row) => row.booked);
                return cost > 0 && booked > 0 ? cost / booked : null;
            },
            format: euroOrBlank,
        },
        { key: "done", label: "1ªs visitas feitas", isNumeric: true, compute: (items) => sumBy(items, (row) => row.done), format: (value) => formatQuantity(value || 0) },
        {
            key: "costPerDone",
            label: "Custo/1ª visita feita",
            isNumeric: true,
            compute: (items) => {
                const cost = sumBy(items, (row) => row.cost);
                const done = sumBy(items, (row) => row.done);
                return cost > 0 && done > 0 ? cost / done : null;
            },
            format: euroOrBlank,
        },
    ];
    const adGroupPivot = buildPivot<AdGroupRow>({
        items: adGroupRows,
        rowLevels: [(row) => row.source, (row) => row.group],
        measures: adGroupMeasures,
        sortRowsBy: [
            (first, second) => AD_GROUP_SOURCE_ORDER.indexOf(first.label) - AD_GROUP_SOURCE_ORDER.indexOf(second.label),
            (first, second) => (Number(second.rawByColumn.leads) || 0) - (Number(first.rawByColumn.leads) || 0),
        ],
    });

    // Estado das leads Facebook e custos por grupo de anúncios
    const adsetStatusLeads: Array<AdsetStatusLead> = adsetStatusLeadsResult.data?.items || [];
    const allAdsetStatusPoints = adsetBookingStatePoints(adsetStatusLeads, facebookAdsetNameById);
    const adsetStatusPoints = pointsWithMoreLeadsThan(allAdsetStatusPoints, ADSET_STATUS_MIN_LEADS);
    const hiddenAdsetsCount = allAdsetStatusPoints.length - adsetStatusPoints.length;
    const hiddenAdsetLeadsCount = sumBy(allAdsetStatusPoints, totalLeadsOfAdsetPoint) - sumBy(adsetStatusPoints, totalLeadsOfAdsetPoint);
    const adsetCostChartPoints = adsetCostPoints(adsetStatusPoints, facebookSpendByAdsetName(facebookSpendByAdsetResult.data || [], facebookAdsetNameByRecordId));
    const adsetChartsHeight = Math.max(240, adsetStatusPoints.length * ADSET_CHART_ROW_HEIGHT + ADSET_CHART_CHROME_HEIGHT);
    const adsetStatusTruncatedWarning = adsetStatusLeadsResult.data?.reachedPageLimit
        ? `Universo truncado nas ${ADSET_STATUS_LEADS_MAX_PAGES * 1000} leads mais antigas do período — encurta o período para ver números completos.`
        : undefined;
    const hiddenAdsetsWarning =
        hiddenAdsetsCount > 0
            ? `Não aparecem ${formatQuantity(hiddenAdsetsCount)} grupos de anúncios com ${ADSET_STATUS_MIN_LEADS} leads ou menos no período (${formatQuantity(hiddenAdsetLeadsCount)} leads no total).`
            : "";

    // Pivots
    const countMeasure: PivotMeasure<any> = { key: "count", label: "Nº tratamentos", isNumeric: true, compute: (items) => items.length, format: (value) => formatQuantity(value || 0) };
    const clientTypePivot = buildPivot<any>({
        items: clientTypeTreatmentsResult.data?.items || [],
        rowLevels: [(treatment) => CLIENT_TYPE_LABELS[clientTypeOfAge(treatment.age_when_started)]],
        measures: [countMeasure],
    });
    const disorderPivot = buildPivot<any>({
        items: disorderTreatmentsResult.data?.items || [],
        rowLevels: [(treatment) => coarseDisorderGroupLabel(coarseDisorderGroupOf(treatment.diagnose_main))],
        measures: [countMeasure],
        sortRowsBy: [(first, second) => (Number(second.rawByColumn.count) || 0) - (Number(first.rawByColumn.count) || 0)],
    });

    // Comparação crianças vs adultos
    const segmentTotalSpend = Number(segmentSpendResult.data?.[0]?.sum?.amount_spent || 0);
    const segmentUniverse = segmentLeadRows(segmentLeadsResult.data?.items || [], FIRST_VISIT_SERVICES);
    const segmentPivot = segmentFunnelPivot(segmentUniverse, segmentTotalSpend);
    const segmentConversionPoints = segmentChartPoints(segmentPivot.rows, SEGMENT_CONVERSION_STAGES);
    const segmentCostPoints = segmentChartPoints(segmentPivot.rows, SEGMENT_COST_STAGES);
    const segmentColumnLabel = (columnKey: string) => segmentPivot.columns.find((column) => column.key === columnKey)?.label || columnKey;
    const segmentTruncatedWarning = segmentLeadsResult.data?.reachedPageLimit
        ? `Universo truncado nas ${SEGMENT_LEADS_MAX_PAGES * 1000} leads mais antigas do período — encurta o período para ver números completos.`
        : undefined;

    // Charts semanais existentes
    const recentLeads = recentLeadsResult.data?.items || [];
    const qualifiedLeadItems = recentLeads
        .filter((lead: { status: string | null }) => ["Good lead", "Qualified"].includes(qualifiedCategoryForStatus(lead.status)))
        .map((lead: { date_created: string; client_type: string | null }) => ({ dateIso: lead.date_created, seriesKey: leadTypeFromClientType(lead.client_type) }));
    const qualifiedByTypePoints = weeklySeriesPoints(qualifiedLeadItems, LEAD_TYPE_KEYS);

    const qualificationItems = recentLeads.map((lead: { date_created: string; status: string | null }) => ({ dateIso: lead.date_created, seriesKey: qualifiedCategoryForStatus(lead.status) }));
    const qualificationPoints = weeklySeriesPoints(qualificationItems, QUALIFIED_CATEGORIES_ORDER);

    const bookedEegItems = (recentBookedEegResult.data?.items || []).map((session: { date_created: string; lead: { client_type: string | null } | null }) => ({
        dateIso: session.date_created,
        seriesKey: leadTypeFromClientType(session.lead?.client_type),
    }));
    const bookedEegByTypePoints = weeklySeriesPoints(bookedEegItems, LEAD_TYPE_KEYS);

    const doneEegItems = (recentDoneEegResult.data?.items || []).map((session: { start_date: string; lead: { client_type: string | null } | null }) => ({
        dateIso: session.start_date,
        seriesKey: leadTypeFromClientType(session.lead?.client_type),
    }));
    const doneEegByTypePoints = weeklySeriesPoints(doneEegItems, LEAD_TYPE_KEYS);

    const bookedEegByClinicItems = (recentBookedEegResult.data?.items || []).map((session: { date_created: string; clinic_id: number | null }) => ({
        dateIso: session.date_created,
        seriesKey: CLINIC_LABELS[Number(session.clinic_id)] || "Outra",
    }));
    const bookedEegByClinicPoints = weeklySeriesPoints(bookedEegByClinicItems, ["Porto", "Braga"]);

    const dailyLeadsPoints = dailyPointsFromCountMap(dailyCountsFromGroupedRows(dailyLeadsResult.data || [], "date_created"), last15DaysRange, "leads", dayLabelWithWeekday);
    const dailyLeadsByClinicPointsData = dailyLeadsByClinicPoints(dailyLeadsByClinicResult.data || [], dailyLeadsByClinicRange, DAILY_LEADS_CLINICS);
    const dailyBookingsCounts = dailyCountsFromGroupedRows(dailyBookingsResult.data || [], "date_created");
    const leadsBookingsPoints = dailyLeadsPoints.map((point, index) => {
        const dayKey = format(addDays(new Date(`${last15DaysRange.gte}T00:00:00`), index), "yyyy-MM-dd");
        const combined = { ...point, bookings: dailyBookingsCounts.get(dayKey) || 0 };
        return combined;
    });

    const revenueByMonth = new Map<number, number>();
    (revenueItemsResult.data?.items || []).forEach(
        (item: { price: number | null; quantity: number | null; discount: number | null; order_id: { order_date: string | null } | null }) => {
            const orderDate = item.order_id?.order_date;
            if (!orderDate) return;
            const monthIndex = Number(orderDate.slice(0, 4)) * 12 + (Number(orderDate.slice(5, 7)) - 1);
            const total = orderItemTotal(Number(item.price || 0), Number(item.quantity || 0), Number(item.discount || 0));
            revenueByMonth.set(monthIndex, (revenueByMonth.get(monthIndex) || 0) + total);
        }
    );
    const revenueThisMonth = Math.round((revenueByMonth.get(nowMonthIndex) || 0) * 100) / 100;
    let revenueLast13Total = 0;
    for (let monthIndex = nowMonthIndex - 12; monthIndex <= nowMonthIndex; monthIndex++) {
        revenueLast13Total += revenueByMonth.get(monthIndex) || 0;
    }
    const revenueMonthlyAverage = Math.round(revenueLast13Total / 13);

    // Texto único da linha de Total, reutilizado por todos os gráficos que a mostram.
    const totalLineText =
        "A linha escura mais grossa é o TOTAL e é sempre a soma exata das restantes linhas do gráfico no mesmo ponto — serve para ver o volume global sem somar as séries de cabeça. Clica na legenda para esconder qualquer linha, o Total incluído.";
    const leadTypeText = 'Tipo de lead pela idade: "Adulto" = mais de 17 anos; "Criança" = 17 ou menos; sem data de nascimento conta como "Desconhecido".';
    // Chips crianças/adultos: existem para vigiar a segmentação das campanhas (o objetivo do negócio é trazer
    // mais crianças do que adultos, e a segmentação pode derivar depressa).
    const leadTypeChipsText =
        'Os chips "Crianças"/"Adultos" repartem o valor pelo campo "tipo de cliente" da lead (leads.client_type) — nunca pela idade, que muitas leads não têm. Números absolutos; "Sem tipo" é o que ainda não foi classificado e só aparece quando existe, de forma a que os três somem sempre ao valor do cartão.';
    const sessionLeadTypeText =
        'As sessões não têm tipo de cliente próprio: cada sessão herda o tipo da LEAD que a originou — a lead ligada à sessão quando existe (1ªs visitas) ou, quando não existe, a lead que originou o cliente do tratamento (sessões a jusante do funil). Só cai em "Sem tipo" quando nenhuma das duas vias dá tipo.';
    const leadTypeChipsWarning =
        'O "tipo de cliente" é preenchido pelo call center quando trabalha a lead, não na criação: nas janelas mais recentes (sobretudo o cartão de hoje) uma fatia grande fica em "Sem tipo" e vai sendo classificada ao longo do dia — medido a 2026-08-03: 10 de 20 leads de hoje sem tipo (50%) contra 42 de 124 nos últimos 7 dias (34%) e ~4% nas avaliações dos últimos 30 dias. Compara Crianças com Adultos entre si e nunca com o total do cartão; para ler a segmentação das campanhas usa a janela de 7 dias ou maior, onde a triagem já está feita. O campo só passou a ser preenchido a partir de setembro de 2025.';
    const qualificationText =
        "Qualificação derivada do estado da lead (réplica exata da fórmula Zoho leads_qualified_categories): lead repetida/cidade errada/número errado/sem resposta/recusada/sem tratamento → Bad lead; 1ª chamada por fazer → Pending contact; a pensar/chamada agendada/para o futuro → Qualified; tudo ok/aguarda EEG/concluído → Good lead; resto → Unknown.";
    // Texto único da definição de "lead qualificada" reutilizado por TODOS os custos por lead do dashboard e pelo gráfico "Leads qualificadas por dia".
    const qualifiedLeadDefinitionText =
        'LEAD QUALIFICADA = tudo o que NÃO é "Bad lead" (Good lead + Qualified + Pending contact + Unknown), ou seja tudo menos lead repetida, cidade errada, número errado, sem resposta após 10 chamadas, recusada, recusámos, submissão negada, engano/não relacionado e sem tratamento. Leads ainda sem triagem (1ª chamada por fazer ou sem estado) CONTAM como qualificadas.';
    const qualifiedLeadDenominatorText = `${qualifiedLeadDefinitionText} O custo continua a ser o gasto blended de anúncios (Facebook + Google) de todo o período, lixo incluído: o custo por lead qualificada mostra quanto se paga por cada lead aproveitável.`;

    const explanations = {
        totalMktCosts: { queryDescription: `Soma do valor gasto em anúncios (Facebook + Google) hoje (${formatDateRangePt(todayRange)}).` },
        costPerLeadToday: {
            queryDescription: `Custo total de anúncios de hoje ÷ leads QUALIFICADAS criadas hoje. Mostra "–" quando não há leads qualificadas hoje. ${qualifiedLeadDenominatorText}`,
            warningNote: `Métrica do dia, com a triagem ainda a meio: as leads criadas hoje que ainda não foram trabalhadas contam como qualificadas (Pending contact / sem estado), por isso o valor TENDE A SUBIR ao longo do dia à medida que o call center marca leads como lixo. Para uma leitura estável usa o "Mkt Cost Per Goal" (mensal) ou a tabela de grupos de anúncios.`,
        },
        costPerBookedLeadToday: {
            queryDescription: `Custo total de anúncios de hoje (Facebook + Google) ÷ leads criadas hoje (${formatDateRangePt(todayRange)}) que já têm marcação — pelo menos uma sessão associada à lead, de qualquer serviço e em qualquer estado (a mesma "marcação" dos gráficos de grupos de anúncios do Facebook). São as mesmas leads do cartão "Leads hoje", só as que já marcaram. Mostra "–" enquanto nenhuma lead de hoje tiver marcação.`,
            warningNote: `Métrica do dia ainda em curso: as leads de hoje continuam a ser contactadas e muitas só marcam nos dias seguintes, por isso o valor começa alto (ou "–") e DESCE à medida que as leads de hoje marcam. Para uma leitura estável usa a tabela e os gráficos de grupos de anúncios.`,
        },
        leadsToday: { queryDescription: `Conta as leads criadas hoje (${formatDateRangePt(todayRange)}). ${leadTypeChipsText}`, warningNote: leadTypeChipsWarning },
        leadsUpdatedToday: { queryDescription: `Conta as leads com última atualização hoje (${formatDateRangePt(todayRange)}).` },
        evalDoneOverScheduledToday: {
            queryDescription: `Mostra FEITAS / AGENDADAS. O primeiro número conta as sessões de Avaliação qEEG - Primeira com início hoje que já foram realizadas, estão em sessão ou com o cliente à espera na clínica — quem já chegou conta como feita. O segundo número: ${describeSessionsCount({
                statuses: EVALUATIONS_HAPPENING_STATUSES,
                services: [FIRST_EEG_SERVICE],
                serviceNames: { [FIRST_EEG_SERVICE]: "Avaliação qEEG - Primeira" },
                range: todayRange,
                excludesInternal: false,
            })} Conta a AGENDA de hoje, independentemente de quando foi marcada; ficam de fora as canceladas, as faltas e a indisponibilidade de staff. As realizadas estão incluídas nas agendadas, por isso o primeiro número nunca passa o segundo. Cada chip segue o mesmo formato feitas/agendadas: Porto e Braga mostram a divisão por clínica. ${leadTypeChipsText} ${sessionLeadTypeText} O detalhe lista as agendadas; a coluna "Estado" mostra quais já foram realizadas, estão em sessão ou à espera na clínica.`,
            warningNote: leadTypeChipsWarning,
        },
        evalBookedToday: {
            queryDescription: `Conta as sessões de Avaliação qEEG - Primeira CRIADAS hoje (qualquer estado), ou seja as marcações feitas hoje — a avaliação em si pode ser para qualquer dia futuro. Porto e Braga mostram a divisão por clínica. ${leadTypeChipsText} ${sessionLeadTypeText}`,
            warningNote: leadTypeChipsWarning,
        },
        leadsLast7: { queryDescription: `Conta as leads criadas nos últimos 7 dias (${formatDateRangePt(last7DaysRange)}). ${leadTypeChipsText}`, warningNote: leadTypeChipsWarning },
        evalDone30: {
            queryDescription: `Conta as sessões de Avaliação qEEG - Primeira realizadas com início nos últimos 30 dias (${formatDateRangePt(last30DaysRange)}). ${leadTypeChipsText} ${sessionLeadTypeText}`,
            warningNote: leadTypeChipsWarning,
        },
        treatmentsNext15: {
            queryDescription: `Para cada tratamento considera a primeira sessão de Neurofeedback ou Psicologia com estado "marcada", "confirmada" ou "realizada". Conta os tratamentos cuja primeira dessas sessões está nos próximos 15 dias (${formatDateRangePt(next15DaysRange)}).`,
            businessNote: `Objetivo: ${TREATMENTS_NEXT_15_DAYS_TARGET}.`,
        },
        treatmentsUntilEndOfMonth: {
            queryDescription: `Para cada tratamento considera a primeira sessão de Neurofeedback ou Psicologia com estado "marcada", "confirmada" ou "realizada". Conta os tratamentos cuja primeira dessas sessões está entre hoje e o fim do mês (${formatDateRangePt({ gte: todayRange.gte, lt: monthRange.lt })}).`,
        },
        evalBookedMonth: { queryDescription: `Conta as sessões de Avaliação qEEG - Primeira marcadas/confirmadas com início este mês (${formatDateRangePt(monthRange)}).` },
        evalDoneMonth: { queryDescription: `Conta as sessões de Avaliação qEEG - Primeira realizadas com início este mês (${formatDateRangePt(monthRange)}).` },
        salesThisMonth: { queryDescription: `Conta os tratamentos com data de início este mês (${formatDateRangePt(monthRange)}).` },
        salesPerMonth: { queryDescription: `Média mensal de tratamentos iniciados nos últimos 13 meses de calendário, incluindo o mês corrente em curso.` },
        evalPerMonth: { queryDescription: `Média mensal de Avaliações qEEG - Primeira realizadas nos últimos 13 meses de calendário, incluindo o mês corrente em curso.` },
        cohortAvgLeads: { queryDescription: `Cohort 13→3 meses: média mensal de leads criadas nesse intervalo de 11 meses.` },
        evalMonthlyChart: { queryDescription: `Avaliações qEEG - Primeira realizadas por mês (início da sessão) e por clínica, últimos 12 meses + mês corrente.` },
        salesMonthlyChart: { queryDescription: `Tratamentos iniciados por mês (data de início) e por clínica (clínica = 1ª sessão de NFB/Psicologia), últimos 12 meses + mês corrente.` },
        treatmentsByTypeChart: {
            queryDescription: `Para cada tratamento considera a primeira sessão de Neurofeedback ou Psicologia já iniciada — com estado "realizada", "à espera na clínica" ou "em sessão" (avaliações, consultas e sessões apenas marcadas não contam) — e agrupa pelo mês dessa primeira sessão, últimos 12 meses + mês corrente. O mês corrente mostra só os tratamentos que já começaram efetivamente. Cada tratamento é classificado pelos serviços de todas as suas sessões de Neurofeedback/Psicologia realizadas ou por vir: só NFB, só Psicologia, ou ambos (NFB + Psi). ${totalLineText}`,
        },
        cohortAvgEvals: { queryDescription: `Cohort de leads criadas entre 13 e 3 meses atrás: média mensal de avaliações (tratamentos ligados às leads via cliente) nesse intervalo de 11 meses.` },
        cohortAvgSales: { queryDescription: `Cohort de leads criadas entre 13 e 3 meses atrás: média mensal de clientes (tratamentos com data de início) originados dessas leads.` },
        cohortLeadsToEvals: { queryDescription: `Cohort 13→3 meses: % de leads que chegaram a avaliação (tratamentos ligados ÷ leads criadas).` },
        cohortLeadsToClients: { queryDescription: `Cohort 13→3 meses: % de leads que chegaram a cliente (tratamentos com data de início ÷ leads criadas).` },
        cohortEvalsToClients: { queryDescription: `Cohort 13→3 meses: % de avaliações que viraram clientes (tratamentos com data de início ÷ tratamentos ligados).` },
        qualifiedByType: { queryDescription: `Leads qualificadas (Good lead + Qualified) por semana de criação, separadas por tipo de lead. ${totalLineText} ${leadTypeText} ${qualificationText}` },
        qualification: { queryDescription: `Leads por semana de criação, empilhadas pela categoria de qualificação. ${qualificationText}` },
        eegBookedByType: { queryDescription: `Sessões de Avaliação qEEG - Primeira CRIADAS por semana, por tipo de lead. ${totalLineText} ${leadTypeText}` },
        eegDoneByType: { queryDescription: `Sessões de Avaliação qEEG - Primeira realizadas por semana (início), por tipo de lead. ${totalLineText} ${leadTypeText}` },
        eegByClinic: {
            queryDescription: `Sessões de Avaliação qEEG - Primeira CRIADAS por semana, por clínica. ${totalLineText}`,
            warningNote: `Só mostra Porto e Braga, e o Total é a soma dessas duas linhas: as marcações noutra clínica (Virtual) NÃO entram em nenhuma das linhas nem no Total, por isso este Total pode ser ligeiramente inferior ao do gráfico "EEG marcados por tipo de lead", que cobre o mesmo universo mas reparte-o por um critério exaustivo. Volume residual — medido a 2026-08-23: 1 sessão Virtual em 630 no período do gráfico.`,
        },
        leadsBookingsDaily: {
            queryDescription: `Últimos 15 dias, por dia: leads criadas vs primeiras visitas marcadas (sessões com lead associada, serviços Avaliação qEEG - Primeira ou Psicologia, por data de criação).`,
        },
        dailyLeadsByClinic: {
            queryDescription: `Número de leads QUALIFICADAS criadas por dia — total e por clínica (Porto, Braga). Tem filtro de datas próprio (por defeito: últimos 14 dias) e não depende de outros filtros do dashboard. O "Total" inclui leads sem clínica atribuída, por isso é ≥ Porto + Braga. Os dias mais recentes podem descer à medida que o call center marca leads como lixo. ${qualifiedLeadDefinitionText}`,
        },
        revenueAvg: {
            queryDescription: `Receita média mensal dos últimos 13 meses de calendário, incluindo o mês corrente em curso, dos itens de encomenda do serviço Avaliação qEEG - Primeira. Valor do item = quantidade × preço − desconto.`,
        },
        revenueThisMonth: {
            queryDescription: `Receita deste mês dos itens de encomenda do serviço Avaliação qEEG - Primeira (mesma definição do cartão da média).`,
        },
        mktCostPerGoal: {
            queryDescription: `Gasto FB+Google do mês (barra) ÷ métricas em cohort pela data de criação da lead: custo por lead QUALIFICADA, por booking (marcadas), por avaliação feita e por tratamento iniciado. Só custo PPC. ${qualifiedLeadDenominatorText}\n\n` +
                `Para saber se o mês já estabilizou, o tooltip mostra ainda: "por realizar" = leads do mês sem avaliação feita com a Avaliação qEEG - Primeira marcada para hoje ou depois; ` +
                `"em aberto" = tratamentos dessas leads ainda sem data de início com estado "Aguarda Relatório", "Consulta de Avaliação realizada", "A pensar", "Aguarda início de tratamento" ou "Ativo" ("Não atenderam" não conta).`,
            warningNote: `O gasto (numerador) é igual ao Zoho ao euro (nov 2025: 10 882 € = FB 8 878,74 + Google 2 003,59). Difere do Zoho de propósito nos DENOMINADORES: o Zoho divide por contagens de REGISTOS de tratamento (nov: 111 registos, mas só 31 iniciados — 80 nunca começaram), dando um "custo por tratamento" artificialmente baixo. Aqui usa-se o funil validado (avaliações feitas=107 / tratamentos iniciados=31), coerente com o "Resultados de marketing".`,
        },
        leadsBookingsCosts: {
            queryDescription: `Por semana: leads criadas, 1ªs visitas marcadas, gasto em anúncios e custo por 1ª visita (gasto da semana ÷ marcações). Cada métrica pela data real do evento.`,
        },
        mktResultsAll: { queryDescription: `Por mês de CRIAÇÃO da lead (cohort, réplica do Zoho): leads criadas, 1ªs avaliações marcadas (lead com estado marcada/feita), avaliações feitas (estado feita) e vendas (tratamentos com data de início originados dessas leads).` },
        marketingEfficiency: {
            queryDescription: `Por mês de criação da lead: % de leads que marcaram 1ª visita, que fizeram avaliação, avaliações que viraram clientes, e leads que viraram clientes.`,
        },
        evalDoneThisMonth: { queryDescription: `Conta as sessões de Avaliação qEEG - Primeira realizadas com início este mês (${formatDateRangePt(monthRange)}).` },
        adGroup: {
            queryDescription: `Leads e marcações por fonte (utm_source: Facebook/Google/Direto) e, dentro de cada fonte: no Facebook por grupo de anúncios (o do anúncio em que a lead clicou, utm_term = id do grupo de anúncios; grupos com o mesmo nome em campanhas diferentes contam como um só; leads sem esse dado ficam em "Sem grupo de anúncios"); no Google e sem tracking por campanha (utm_campaign), porque as leads do Google trazem a palavra-chave e não o grupo de anúncios. O custo é o gasto em anúncios (marketing_spend) desse grupo de anúncios no Facebook, ou da campanha do anúncio no Google. Marcação = lead com 1ª avaliação marcada (progress); feita = 1ª avaliação realizada. A coluna "Custo/lead qualificada" divide o custo pelas leads qualificadas (coluna "Leads qualif."), não por todas as leads. ${qualifiedLeadDenominatorText}`,
            warningNote: `Difere do Zoho de propósito — o widget Zoho tem falhas de tracking assumidas: o conector de custos Facebook do Zoho está vazio (custos FB em branco) e o Zoho deixa cair campanhas reais COM gasto (ex.: "us_vs_them_meds" — 16 leads e centenas de € numa semana) em "Direct / no tracking". Aqui cada lead é atribuída ao seu grupo de anúncios (Facebook) ou campanha (Google) e mostra-se o gasto real do Directus (completo) — logo, mais completo e fiável.`,
        },
        adsetStatus: {
            metricDefinitions: [
                { metric: "Realizada", definition: "a última marcação foi realizada (ou o cliente está em sessão)." },
                { metric: "Marcada, por realizar", definition: "a última marcação está marcada, confirmada ou com o cliente à espera na clínica." },
                { metric: "Cancelada/faltou, sem remarcação", definition: "a última marcação foi cancelada (pelo cliente ou por indisponibilidade nossa) ou o cliente faltou, e não voltou a marcar." },
                { metric: "Sem marcação", definition: "a lead não tem nenhuma sessão associada." },
            ],
            queryDescription: `Leads do Facebook (utm_source = facebook) criadas em ${formatDateRangePt(adsetStatusRange)}, uma barra por grupo de anúncios, ordenadas pelo total de leads. O grupo de anúncios de cada lead é o do anúncio em que clicou (utm_term = id do grupo de anúncios no Facebook); grupos com o mesmo nome em campanhas diferentes contam como um só. Leads sem esse dado ficam em "Sem grupo de anúncios". Cada lead conta uma única vez, pelo estado da sua ÚLTIMA marcação: a sessão associada à lead criada mais recentemente, de qualquer serviço. Uma lead que cancelou e voltou a marcar fica no estado da marcação nova.`,
            businessNote: `A soma das barras com as leads dos grupos de anúncios escondidos é igual à linha "Facebook" da tabela acima (coluna "Leads"), para o mesmo período. A tabela usa os mesmos grupos de anúncios do Facebook, mas as suas colunas de marcações usam o progresso da lead e não a última sessão, por isso os números de marcações não se comparam diretamente.`,
            warningNote: `${adsetStatusTruncatedWarning ? `${adsetStatusTruncatedWarning} ` : ""}${hiddenAdsetsWarning ? `${hiddenAdsetsWarning} ` : ""}As leads criadas há pouco ainda podem ter a visita por realizar ou ainda não ter marcado: em períodos recentes a parte "Realizada" é um mínimo.`,
        },
        adsetCost: {
            metricDefinitions: [
                { metric: "Custo/lead potencial", definition: "gasto do grupo de anúncios ÷ as suas leads qualificadas (todas exceto as Bad lead; as ainda por triar contam)." },
                { metric: "Custo/marcação", definition: "gasto ÷ leads com pelo menos uma marcação (realizada, por realizar ou cancelada/faltou no gráfico ao lado)." },
                { metric: "Custo/marcação realizada", definition: "gasto ÷ leads cuja última marcação foi realizada (a parte verde do gráfico ao lado)." },
            ],
            queryDescription: `Gasto em anúncios do Facebook de ${formatDateRangePt(adsetStatusRange)} por grupo de anúncios (marketing_spend pela data do gasto), a dividir pelas leads desse grupo criadas no mesmo período. Os mesmos grupos de anúncios e pela mesma ordem do gráfico ao lado; grupos com o mesmo nome em campanhas diferentes somam o gasto. Por baixo do nome de cada grupo aparece o gasto total do período.`,
            businessNote: `Barra vazia = sem custo calculável: o grupo não tem gasto atribuído (ex.: "Sem grupo de anúncios") ou ainda não tem nenhuma lead nessa etapa.`,
            warningNote: `${hiddenAdsetsWarning ? `${hiddenAdsetsWarning} ` : ""}O gasto conta pela data do gasto e as leads pela data de criação: num período curto, ou no início de um grupo de anúncios, o custo por marcação realizada fica alto porque as visitas ainda não aconteceram.`,
        },
        clientTypePivot: { queryDescription: `Tratamentos iniciados (por data de início) por tipo de cliente: Adulto > 18 anos; Criança ≤ 18 (ou sem idade registada).` },
        disorderPivot: { queryDescription: `Tratamentos iniciados (por data de início) por grupo de perturbação principal (PHDA / Autismo / Ansiedade / Outros).` },
        segmentFunnel: {
            queryDescription: `Leads criadas no período (${formatDateRangePt(segmentRange)}), separadas pelo tipo de cliente do campo da lead: Criança, Adulto e "Desconhecido" (leads sem tipo preenchido — tipicamente as que nunca responderam). Por segmento: Marcaram = leads com pelo menos uma sessão de 1ª visita (Avaliação qEEG - Primeira ou Psicologia) criada, em qualquer estado, alguma vez; Sessões resolvidas = as sessões dessas leads que já tiveram desfecho (realizada/em sessão + cancelada/não compareceu); % comparência (sessão) = realizadas ÷ resolvidas, pelo que cada remarcação conta contra o rácio; Compareceram = leads com pelo menos uma sessão realizada, independentemente de quantas vezes remarcaram; Compraram = leads cujo cliente tem um tratamento com data de início real.`,
            businessNote: `O custo é o gasto em anúncios (Facebook + Google) do MESMO período, repartido pró-rata pela quota de leads de cada segmento (TODAS as leads, lixo incluído). O Custo/lead qualificada divide essa fatia apenas pelas leads qualificadas do segmento, por isso já DIFERE entre crianças e adultos: um segmento que gere mais lixo paga mais por cada lead aproveitável. ${qualifiedLeadDenominatorText}`,
            warningNote: `${segmentTruncatedWarning ? `${segmentTruncatedWarning} ` : ""}Tudo é atribuído à data de CRIAÇÃO da lead: uma lead criada nos últimos ~3 meses ainda está a converter (há casos com 4 meses entre a criação da lead e o início do tratamento), por isso as colunas de compra dos períodos recentes são um MÍNIMO e não um valor final. Sessões canceladas por indisponibilidade nossa e sessões ainda por realizar ficam fora do rácio de comparência. A linha Total inclui o segmento Desconhecido e por isso fica abaixo dos dois segmentos reais — compara Criança com Adulto, nunca com o Total. Como o gasto das leads sem tipo fica parqueado na linha Desconhecido, os dois segmentos reais só suportam a SUA quota do gasto (hoje ~2/3): o Custo/avaliação e o Custo/venda de Criança e Adulto são um LIMITE INFERIOR. E atenção à janela: o campo "tipo de cliente" da lead só passou a ser preenchido a partir de SETEMBRO de 2025 (em agosto de 2025 e antes, praticamente todas as leads ficam em Desconhecido por falta de dados, não por serem leads mortas). Períodos que incluam esses meses inflacionam o Desconhecido; para uma leitura com o campo sempre preenchido usa "Este ano" ou um período personalizado a começar em setembro de 2025.`,
        },
        segmentConversion: {
            queryDescription: `As mesmas percentagens da tabela acima, lado a lado por etapa do funil: % marcação (leads que marcaram ÷ leads criadas), % comparência ao nível da SESSÃO (penaliza remarcações), % de leads que chegaram efetivamente a fazer a avaliação, % de avaliações feitas que viraram compra e % de leads que viraram compra. Apenas Criança e Adulto — o segmento Desconhecido fica de fora da comparação.`,
            warningNote: `Percentagens sobre leads criadas no período (${formatDateRangePt(segmentRange)}); as leads mais recentes ainda estão a converter.`,
        },
        segmentCost: {
            queryDescription: `Gasto em anúncios (Facebook + Google) do período repartido pró-rata pela quota de leads de cada segmento, dividido por lead criada, por lead que compareceu à avaliação e por lead que comprou. Apenas Criança e Adulto.`,
            businessNote: `O gasto é blended (sem atribuição por segmento), repartido pela quota de TODAS as leads do segmento. O Custo/lead qualificada varia com a taxa de lixo de cada segmento; o Custo/avaliação e o Custo/venda mostram qual dos segmentos é mais caro de converter. ${qualifiedLeadDenominatorText}`,
            warningNote: `O gasto das leads sem tipo preenchido fica na linha Desconhecido da tabela, pelo que estes valores são um LIMITE INFERIOR: os dois segmentos suportam apenas a sua quota do gasto (hoje ~2/3 do total). A comparação ENTRE crianças e adultos mantém-se válida — ambos são subestimados na mesma proporção.`,
        },
    };

    const CHIP_LEAD_FIELDS = ["client_type", "status"];
    // Grupo de anúncios antes da utm_campaign (mais visível) — só neste dashboard, por isso colunas/fields locais (o
    // leadsDrillColumns partilhado também serve a receção).
    const facebookAdsetDrillColumn = {
        key: "facebookAdset",
        label: "Grupo de anúncios",
        valueFor: (lead: any) => facebookAdsetNameOfLead(lead, facebookAdsetNameById),
    };
    const leadsDrillColumnsWithChips = leadsDrillColumns.flatMap((column) => {
        if (CHIP_LEAD_FIELDS.includes(column.key)) return [{ ...column, chipField: leadFields.find((field) => field.field === column.key) }];
        if (column.key === "utm_campaign") return [facebookAdsetDrillColumn, column];
        return [column];
    });
    const leadsDrillBase = { fieldsQuery: `${LEADS_DRILL_FIELDS},utm_term`, defaultSort: "-date_created", columns: leadsDrillColumnsWithChips, rowRoute: leadRoute };
    const sessionsDrillBase = { fieldsQuery: SESSIONS_DRILL_FIELDS, defaultSort: "start_date", columns: sessionsDrillColumns, rowRoute: sessionRoute };
    // "Avaliações marcadas hoje": acrescenta as colunas "Grupo de anúncios" (da lead da sessão) e "Criado por" (user_created) — só neste drill, por isso
    // colunas/fields locais (não mexer no sessionsDrillColumns partilhado por daily-analysis/receção/nfb).
    const sessionFacebookAdsetDrillColumn = {
        key: "facebookAdset",
        label: "Grupo de anúncios",
        valueFor: (session: any) => (session.lead ? facebookAdsetNameOfLead(session.lead, facebookAdsetNameById) : null),
    };
    const SESSION_LEAD_ADSET_FIELDS = "lead.utm_source,lead.utm_term";
    const evalBookedDrillBase = {
        fieldsQuery: `${SESSIONS_DRILL_FIELDS},user_created.first_name,user_created.last_name,${SESSION_LEAD_ADSET_FIELDS}`,
        defaultSort: "start_date",
        columns: [...sessionsDrillColumns, sessionFacebookAdsetDrillColumn, { key: "createdBy", label: "Criado por", valueFor: sessionCreatedByName }],
        rowRoute: sessionRoute,
    };
    const evalWithAdsetDrillBase = {
        fieldsQuery: `${SESSIONS_DRILL_FIELDS},${SESSION_LEAD_ADSET_FIELDS}`,
        defaultSort: "start_date",
        columns: [...sessionsDrillColumns.filter((column) => column.key !== "client"), sessionFacebookAdsetDrillColumn],
        rowRoute: sessionRoute,
    };
    const upcomingDrillColumns = [
        { key: "clientName", label: "Cliente" },
        { key: "firstSessionDate", label: "1ª sessão NFB/Psi", valueFor: (row: any) => formatDrillDateTime(row.firstSessionDate) },
    ];

    // Drill por barra dos charts por clínica: mês clicado → sessões/tratamentos desse mês.
    const evalByClinicBarDrill = (point: Record<string, string | number>) => {
        const range = monthRangeFromIndex(Number(point.monthIndex));
        const filter = { _and: [{ status: { _eq: "done" } }, { start_date: { _gte: range.gte, _lt: range.lt } }] };
        const drill = drillDownFromAggregate(evaluationsCountQuery, filter, {
            title: `Avaliações feitas — ${point.monthLabel}`,
            queryDescription: `Avaliações qEEG - Primeira realizadas em ${point.monthLabel}.`,
            ...sessionsDrillBase,
        });
        return drill;
    };
    const salesByClinicBarDrill = (point: Record<string, string | number>) => {
        const range = monthRangeFromIndex(Number(point.monthIndex));
        const filter = { date_of_start: { _gte: range.gte, _lt: range.lt } };
        const drill = drillDownFromAggregate(treatmentsStartedCountQuery, filter, {
            title: `Vendas — ${point.monthLabel}`,
            queryDescription: `Tratamentos iniciados em ${point.monthLabel}.`,
            fieldsQuery: TREATMENTS_DRILL_FIELDS,
            defaultSort: "date_of_start",
            columns: treatmentsDrillColumns,
            rowRoute: treatmentRoute,
        });
        return drill;
    };

    // Drill por célula da tabela de grupos de anúncios: fonte/grupo → leads dessa fonte/grupo (dados já obtidos).
    const progressLabel = (progress: string | null) =>
        progress === "evaluation_done" ? "1ª visita feita" : progress === "evaluation_booked" ? "1ª visita marcada" : "Sem marcação";
    const adGroupCellDrill = (node: { path: Array<string> }) => {
        const source = node.path[0];
        const group = node.path.length > 1 ? node.path[1] : null;
        const matchingLeads = (adGroupLeadsResult.data?.items || []).filter((lead: any) => {
            if (sourceLabelOf(lead.utm_source) !== source) return false;
            if (group !== null && adGroupLabelOfLead(lead, facebookAdsetNameById) !== group) return false;
            return true;
        });
        const drill = {
            kind: "rows" as const,
            title: `Leads — ${node.path.join(" › ")}`,
            queryDescription: `Leads criadas em ${node.path.join(" › ")} (${formatDateRangePt(adGroupRange)}).`,
            columns: [
                { key: "id", label: "Lead" },
                { key: "facebookAdset", label: "Grupo de anúncios" },
                { key: "utm_campaign", label: "Campanha" },
                { key: "statusLabel", label: "Estado" },
            ],
            rows: matchingLeads.map((lead: any) => ({
                id: lead.id,
                utm_campaign: lead.utm_campaign || "—",
                facebookAdset: facebookAdsetNameOfLead(lead, facebookAdsetNameById) || "—",
                statusLabel: progressLabel(lead.progress),
            })),
            defaultSortKey: "id",
            rowRoute: (row: any) => `/leads/${row.id}`,
        };
        return drill;
    };

    // Drill por barra do estado das leads Facebook: grupo de anúncios → as suas leads, com o estado e a data da última marcação.
    const adsetStatusBarDrill = (point: Record<string, string | number>) => {
        const adset = String(point.adset);
        const adsetLeads = adsetStatusLeads.filter((lead) => adsetLabelOf(lead.utm_term, facebookAdsetNameById) === adset);
        const drill = {
            kind: "rows" as const,
            title: `Leads Facebook — ${adset}`,
            queryDescription: `Leads do Facebook do grupo de anúncios "${adset}" criadas em ${formatDateRangePt(adsetStatusRange)}, com o estado da última marcação.`,
            headerChips: ADSET_STATUS_SERIES.map((serie) => `${serie.label}: ${formatQuantity(Number(point[serie.key]) || 0)}`),
            columns: [
                { key: "id", label: "Lead" },
                { key: "name", label: "Nome" },
                { key: "date_created", label: "Criada em", valueFor: (row: any) => formatDrillDateTime(row.date_created) },
                { key: "utm_campaign", label: "Campanha" },
                { key: "status", label: "Estado da lead", chipField: leadFields.find((field) => field.field === "status") },
                { key: "stateLabel", label: "Estado da marcação" },
                { key: "latestBookingStart", label: "Data da última marcação", valueFor: (row: any) => formatDrillDateTime(row.latestBookingStart) },
            ],
            rows: adsetLeads.map((lead) => ({
                id: lead.id,
                name: [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() || null,
                date_created: lead.date_created,
                utm_campaign: lead.utm_campaign || AD_GROUP_CAMPAIGN_FALLBACK,
                status: lead.status ?? null,
                stateLabel: bookingStateLabelOf(leadBookingStateOf(lead.sessions)),
                latestBookingStart: latestCreatedSession(lead.sessions)?.start_date ?? null,
            })),
            defaultSortKey: "id",
            rowRoute: leadRoute,
        };
        return drill;
    };

    // Drill por célula dos pivots (tipo de cliente / grupo de perturbação): grupo → tratamentos (dados já obtidos).
    const treatmentPivotDrill = (label: string, items: Array<any>, periodRange: { gte: string; lt: string }) => {
        const drill = {
            kind: "rows" as const,
            title: `Tratamentos — ${label}`,
            queryDescription: `Tratamentos iniciados (${label}) em ${formatDateRangePt(periodRange)}.`,
            columns: [
                { key: "id", label: "Tratamento" },
                { key: "date_of_start", label: "Início", valueFor: (row: any) => formatDrillDate(row.date_of_start) },
                { key: "age", label: "Idade", isNumeric: true },
            ],
            rows: items.map((treatment: any) => ({ id: treatment.id, date_of_start: treatment.date_of_start, age: treatment.age_when_started ?? null })),
            defaultSortKey: "date_of_start",
            rowRoute: (row: any) => `/treatments/${row.id}`,
        };
        return drill;
    };
    const clientTypeCellDrill = (node: { path: Array<string> }) => {
        const label = node.path[0];
        const items = (clientTypeTreatmentsResult.data?.items || []).filter((treatment: any) => CLIENT_TYPE_LABELS[clientTypeOfAge(treatment.age_when_started)] === label);
        return treatmentPivotDrill(label, items, clientTypeRange);
    };
    const disorderCellDrill = (node: { path: Array<string> }) => {
        const label = node.path[0];
        const items = (disorderTreatmentsResult.data?.items || []).filter((treatment: any) => coarseDisorderGroupLabel(coarseDisorderGroupOf(treatment.diagnose_main)) === label);
        return treatmentPivotDrill(label, items, disorderRange);
    };

    // Drill por célula da comparação de segmentos: cada coluna abre as leads do seu próprio numerador
    // (ex.: Custo/venda abre as leads que compraram), a partir das MESMAS linhas que produziram o número.
    // Nas colunas medidas em SESSÕES a modal continua a listar LEADS (é a unidade do universo), por isso o
    // "N registos" é menor do que a célula — as somas de sessões vão nos chips do cabeçalho, que é a
    // convenção do framework para "contagem de linhas não verifica uma soma".
    const segmentCellDrill = (node: PivotNode<SegmentLeadRow>, columnKey: string) => {
        const leadRows = segmentDrillRows(node.items, columnKey);
        const isSessionLevelColumn = SEGMENT_SESSION_LEVEL_COLUMNS.includes(columnKey);
        const sessionChips = isSessionLevelColumn
            ? [
                  `Sessões resolvidas: ${formatQuantity(sumBy(leadRows, (row) => row.resolvedSessions))}`,
                  `Sessões feitas: ${formatQuantity(sumBy(leadRows, (row) => row.attendedSessions))}`,
              ]
            : [];
        const drill = {
            kind: "rows" as const,
            title: `${node.label} — ${segmentColumnLabel(columnKey)}`,
            queryDescription: `Leads de tipo "${node.label}" criadas em ${formatDateRangePt(segmentRange)} que contam para "${segmentColumnLabel(columnKey)}".${
                isSessionLevelColumn
                    ? " Cada linha é uma LEAD, mas esta coluna mede SESSÕES: o número de registos é o das leads e é menor do que o valor da célula (uma lead pode ter várias sessões). As somas de sessões estão nos chips acima."
                    : ""
            }`,
            headerChips: sessionChips,
            columns: [
                { key: "leadName", label: "Lead" },
                { key: "dateCreated", label: "Criada", valueFor: (row: any) => formatDrillDateTime(row.dateCreated) },
                { key: "status", label: "Estado", chipField: leadFields.find((field) => field.field === "status") },
                { key: "resolvedSessions", label: "Sessões resolvidas", isNumeric: true },
                { key: "attendedSessions", label: "Sessões feitas", isNumeric: true },
                { key: "attended", label: "Compareceu", valueFor: (row: any) => (row.attended ? "Sim" : "Não") },
                { key: "bought", label: "Comprou", valueFor: (row: any) => (row.bought ? "Sim" : "Não") },
            ],
            rows: leadRows.map((leadRow) => ({ ...leadRow, id: leadRow.leadId })),
            defaultSortKey: "dateCreated",
            rowRoute: (row: any) => `/leads/${row.id}`,
        };
        return drill;
    };

    const drills = {
        totalMktCosts: {
            kind: "rows" as const,
            title: "Custo de marketing hoje",
            queryDescription: `Gasto de anúncios de hoje (${formatDateRangePt(todayRange)}) somado por grupo de anúncios (adset), agrupado por plataforma (Facebook, Google) e ordenado por gasto.`,
            columns: [
                { key: "platform", label: "Plataforma" },
                { key: "adsetName", label: "Grupo de anúncios" },
                { key: "amount_spent", label: "Gasto", isNumeric: true, valueFor: (row: any) => formatEuros(Number(row.amount_spent || 0)) },
            ],
            rows: adsetSpendRows,
            defaultSortKey: "platform",
            defaultPageSize: 100,
            headerChips: spendDrillHeaderChips,
        },
        leadsToday: drillDownFromAggregate(leadsCountQuery, leadsTodayFilter, { title: "Leads hoje", queryDescription: explanations.leadsToday.queryDescription, ...leadsDrillBase }),
        qualifiedLeadsToday: drillDownFromAggregate(leadsCountQuery, qualifiedLeadsTodayFilter, { title: "Leads qualificadas hoje", queryDescription: explanations.costPerLeadToday.queryDescription, ...leadsDrillBase }),
        bookedLeadsToday: drillDownFromAggregate(leadsCountQuery, bookedLeadsTodayFilter, { title: "Leads de hoje com marcação", queryDescription: explanations.costPerBookedLeadToday.queryDescription, ...leadsDrillBase }),
        leadsUpdatedToday: drillDownFromAggregate(leadsCountQuery, leadsUpdatedTodayFilter, { title: "Leads atualizadas hoje", queryDescription: explanations.leadsUpdatedToday.queryDescription, ...leadsDrillBase }),
        evalDoneOverScheduledToday: drillDownFromAggregate(evaluationsCountQuery, evalScheduledTodayFilter, { title: "Avaliações agendadas para hoje", queryDescription: explanations.evalDoneOverScheduledToday.queryDescription, ...evalWithAdsetDrillBase }),
        evalBookedToday: drillDownFromAggregate(evaluationsBookedByClinicQuery, evalBookedTodayFilter, { title: "Marcações de avaliação criadas hoje", queryDescription: explanations.evalBookedToday.queryDescription, ...evalBookedDrillBase }),
        evalBookedTodayPorto: drillDownFromAggregate(evaluationsBookedByClinicQuery, combineFilters(evalBookedTodayFilter, { clinic_id: { _eq: 1 } }), { title: "Marcações de avaliação criadas hoje — Porto", queryDescription: explanations.evalBookedToday.queryDescription, ...evalBookedDrillBase }),
        evalBookedTodayBraga: drillDownFromAggregate(evaluationsBookedByClinicQuery, combineFilters(evalBookedTodayFilter, { clinic_id: { _eq: 3 } }), { title: "Marcações de avaliação criadas hoje — Braga", queryDescription: explanations.evalBookedToday.queryDescription, ...evalBookedDrillBase }),
        leadsLast7: drillDownFromAggregate(leadsCountQuery, leadsLast7Filter, { title: "Leads últimos 7 dias", queryDescription: explanations.leadsLast7.queryDescription, ...leadsDrillBase }),
        evalDone30: drillDownFromAggregate(evaluationsCountQuery, evalDone30Filter, { title: "Avaliações feitas últimos 30 dias", queryDescription: explanations.evalDone30.queryDescription, ...sessionsDrillBase }),
        treatmentsNext15: {
            kind: "rows" as const,
            title: "Tratamentos a começar (15 dias)",
            queryDescription: explanations.treatmentsNext15.queryDescription,
            columns: upcomingDrillColumns,
            rows: upcomingDrillRowsFor(upcomingNext15),
            defaultSortKey: "firstSessionDate",
            rowRoute: (row: any) => `/treatments/${row.id}`,
        },
        treatmentsUntilEndOfMonth: {
            kind: "rows" as const,
            title: "Tratamentos a começar (até fim do mês)",
            queryDescription: explanations.treatmentsUntilEndOfMonth.queryDescription,
            columns: upcomingDrillColumns,
            rows: upcomingDrillRowsFor(upcomingUntilEndOfMonth),
            defaultSortKey: "firstSessionDate",
            rowRoute: (row: any) => `/treatments/${row.id}`,
        },
        evalBookedMonth: drillDownFromAggregate(evaluationsCountQuery, evalBookedMonthFilter, { title: "Avaliações marcadas este mês", queryDescription: explanations.evalBookedMonth.queryDescription, ...sessionsDrillBase }),
        evalDoneMonth: drillDownFromAggregate(evaluationsCountQuery, evalDoneMonthFilter, { title: "Avaliações feitas este mês", queryDescription: explanations.evalDoneMonth.queryDescription, ...sessionsDrillBase }),
        salesThisMonth: drillDownFromAggregate(treatmentsStartedCountQuery, salesMonthFilter, {
            title: "Vendas este mês",
            queryDescription: explanations.salesThisMonth.queryDescription,
            fieldsQuery: TREATMENTS_DRILL_FIELDS,
            defaultSort: "date_of_start",
            columns: treatmentsDrillColumns,
            rowRoute: treatmentRoute,
        }),
        revenueThisMonth: {
            kind: "rows" as const,
            title: "Receita avaliações este mês",
            queryDescription: `${explanations.revenueThisMonth.queryDescription} Cada linha é um item de encomenda; a soma da coluna Total corresponde ao valor do cartão.`,
            columns: [
                { key: "orderDate", label: "Data da encomenda", valueFor: (row: any) => formatDrillDate(row.orderDate) },
                { key: "orderId", label: "Encomenda" },
                { key: "quantity", label: "Quantidade", isNumeric: true },
                { key: "total", label: "Total", isNumeric: true, valueFor: (row: any) => formatEuros(row.total) },
            ],
            rows: (revenueItemsResult.data?.items || [])
                .filter((item: any) => {
                    const orderDate = item.order_id?.order_date;
                    if (!orderDate) return false;
                    const monthIndex = Number(orderDate.slice(0, 4)) * 12 + (Number(orderDate.slice(5, 7)) - 1);
                    return monthIndex === nowMonthIndex;
                })
                .map((item: any) => ({
                    orderId: item.order_id?.id ?? null,
                    orderDate: item.order_id?.order_date ?? null,
                    quantity: Number(item.quantity || 0),
                    total: orderItemTotal(Number(item.price || 0), Number(item.quantity || 0), Number(item.discount || 0)),
                })),
            defaultSortKey: "orderDate",
            rowRoute: (row: any) => (row.orderId ? `/orders/${row.orderId}` : null),
        },
    };

    return (
        <DashboardPage globalFilters={[]} values={values} onChange={setValue}>
            <DashboardSection title="Análises de quando aconteceu">
                <Box
                    display="grid"
                    gap={3}
                    sx={{
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
                        gridTemplateAreas: { lg: `"cost1 leads evaluations updated" "cost2 costBooked booked updated"` },
                    }}
                >
                    <Box sx={{ gridArea: { lg: "cost1" } }}>
                        <StatCard label="Custo total de marketing" value={totalCostTodayValue} color={DASHBOARD_COLORS.orange} explanation={explanations.totalMktCosts} isLoading={spendTodayResult.isLoading} drillDown={spendTodayRowsResult.data ? drills.totalMktCosts : undefined} chips={spendTodayRowsResult.data ? spendTodayChips : undefined} sx={{ width: "100%" }} />
                    </Box>
                    <Box sx={{ gridArea: { lg: "leads" } }}>
                        <StatCard label="Leads hoje" value={leadsTodayResult.data ? leadsTodayCount : undefined} color={DASHBOARD_COLORS.neutral} explanation={explanations.leadsToday} isLoading={leadsTodayResult.isLoading} drillDown={drills.leadsToday} chips={leadsTodayByClinicResult.data ? leadsTodayChips : undefined} secondaryChips={leadsTodayByTypeResult.data ? leadsTodayTypeChips : undefined} sx={{ width: "100%" }} />
                    </Box>
                    <Box sx={{ gridArea: { lg: "evaluations" } }}>
                        <StatCard
                            label="Avaliações hoje (feitas / agendadas)"
                            value={evalDoneOverScheduledTodayValue}
                            color={DASHBOARD_COLORS.neutral}
                            explanation={explanations.evalDoneOverScheduledToday}
                            isLoading={evalDoneTodayResult.isLoading || evalScheduledTodayResult.isLoading}
                            drillDown={drills.evalDoneOverScheduledToday}
                            chips={evalDoneTodayByClinicResult.data && evalScheduledTodayByClinicResult.data ? evalDoneOverScheduledTodayChips : undefined}
                            secondaryChips={evalDoneTodayByTypeResult.data && evalScheduledTodayByTypeResult.data ? evalDoneOverScheduledTodayTypeChips : undefined}
                            sx={{ width: "100%" }}
                        />
                    </Box>
                    <Box sx={{ gridArea: { lg: "booked" } }}>
                        <StatCard label="Marcações de avaliação criadas hoje" value={evalBookedTodayResult.data ? evalBookedTodayTotal : undefined} color={DASHBOARD_COLORS.neutral} explanation={explanations.evalBookedToday} isLoading={evalBookedTodayResult.isLoading} drillDown={drills.evalBookedToday} chips={evalBookedTodayResult.data ? evalBookedTodayChips : undefined} secondaryChips={evalBookedTodayByTypeResult.data ? evalBookedTodayTypeChips : undefined} sx={{ width: "100%" }} />
                    </Box>
                    <Box sx={{ gridArea: { lg: "updated" } }}>
                        <StatCard label="Leads atualizadas hoje" value={leadsUpdatedTodayResult.data ? singleCount(leadsUpdatedTodayResult.data) : undefined} color={DASHBOARD_COLORS.neutral} explanation={explanations.leadsUpdatedToday} isLoading={leadsUpdatedTodayResult.isLoading} drillDown={drills.leadsUpdatedToday} sx={{ width: "100%" }} />
                    </Box>
                    <Box sx={{ gridArea: { lg: "costBooked" } }}>
                        <StatCard
                            label="Custo por lead marcada (hoje)"
                            value={costPerBookedLeadTodayValue}
                            color={DASHBOARD_COLORS.orange}
                            explanation={explanations.costPerBookedLeadToday}
                            isLoading={spendTodayResult.isLoading || bookedLeadsTodayResult.isLoading}
                            drillDown={drills.bookedLeadsToday}
                            chips={bookedLeadsTodayResult.data && leadsTodayResult.data ? costPerBookedLeadTodayChips : undefined}
                            sx={{ width: "100%" }}
                        />
                    </Box>
                    <Box sx={{ gridArea: { lg: "cost2" } }}>
                        <StatCard label="Custo por lead qualificada (hoje)" value={costPerLeadTodayValue} color={DASHBOARD_COLORS.orange} explanation={explanations.costPerLeadToday} isLoading={spendTodayResult.isLoading || qualifiedLeadsTodayResult.isLoading} drillDown={drills.qualifiedLeadsToday} chips={qualifiedLeadsTodayResult.data && leadsTodayResult.data ? costPerLeadTodayChips : undefined} sx={{ width: "100%" }} />
                    </Box>
                </Box>

                <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(260px, 1fr))" gap={3}>
                    <GaugeCard title="Leads últimos 7 dias" value={leadsLast7Result.data ? singleCount(leadsLast7Result.data) : undefined} goal={LEADS_LAST_7_DAYS_GOAL} explanation={explanations.leadsLast7} isLoading={leadsLast7Result.isLoading} isError={leadsLast7Result.isError} drillDown={drills.leadsLast7} chips={leadsLast7ByTypeResult.data ? leadsLast7Chips : undefined} />
                    <GaugeCard title="Avaliações feitas últimos 30 dias" value={evalDone30Result.data ? singleCount(evalDone30Result.data) : undefined} goal={EVALUATIONS_DONE_30_DAYS_GOAL} explanation={explanations.evalDone30} isLoading={evalDone30Result.isLoading} isError={evalDone30Result.isError} drillDown={drills.evalDone30} chips={evalDone30ByTypeResult.data ? evalDone30Chips : undefined} />
                    <UpcomingEvaluationsGauges evaluationsDrillBase={evalWithAdsetDrillBase} />
                </Box>

                <HorizontalGaugeCard title="Tratamentos a começar (15 dias)" value={treatmentsFirstSessionResult.data ? treatmentsNext15 : undefined} goal={TREATMENTS_NEXT_15_DAYS_TARGET} explanation={explanations.treatmentsNext15} isLoading={treatmentsFirstSessionResult.isLoading} drillDown={drills.treatmentsNext15} />

                <WidgetCard
                    title="Leads qualificadas por dia (por clínica)"
                    explanation={explanations.dailyLeadsByClinic}
                    bodyHeight={320}
                    isLoading={dailyLeadsByClinicResult.isLoading}
                    isError={dailyLeadsByClinicResult.isError}
                    headerControls={
                        <DashboardDateFilter label="Período" filterKey="dailyLeadsPeriod" values={dailyLeadsFilterValues} onChange={setLocalFilter(setDailyLeadsFilterValues)} presets={DAILY_LEADS_DATE_PRESETS} />
                    }
                >
                    <TimeSeriesChart points={dailyLeadsByClinicPointsData} series={DAILY_LEADS_BY_CLINIC_SERIES} height={320} showPointLabels />
                </WidgetCard>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <WidgetCard title="Leads qualificadas por tipo (semanal)" explanation={explanations.qualifiedByType} bodyHeight={320} isLoading={recentLeadsResult.isLoading} isError={recentLeadsResult.isError}>
                            <TimeSeriesChart points={qualifiedByTypePoints} series={LEAD_TYPE_SERIES_WITH_TOTAL} height={320} showPointLabels />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <WidgetCard title="Qualificação das leads (semanal)" explanation={explanations.qualification} bodyHeight={320} isLoading={recentLeadsResult.isLoading} isError={recentLeadsResult.isError}>
                            <StackedBarChart
                                points={qualificationPoints}
                                series={[
                                    { key: "Bad lead", label: "Bad lead", color: DASHBOARD_COLORS.cancelled },
                                    { key: "Good lead", label: "Good lead", color: DASHBOARD_COLORS.done },
                                    { key: "Pending contact", label: "Pending contact", color: DASHBOARD_COLORS.orange },
                                    { key: "Qualified", label: "Qualified", color: DASHBOARD_COLORS.highlight },
                                    { key: "Unknown", label: "Unknown", color: GAUGE_MIDDLE_COLOR },
                                ]}
                                categoryKey="monthLabel"
                                height={320}
                                showBarLabels
                                showTotalLabel
                            />
                        </WidgetCard>
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <WidgetCard title="EEG marcados por tipo de lead (semanal)" explanation={explanations.eegBookedByType} bodyHeight={300} isLoading={recentBookedEegResult.isLoading} isError={recentBookedEegResult.isError}>
                            <TimeSeriesChart points={bookedEegByTypePoints} series={LEAD_TYPE_SERIES_WITH_TOTAL} height={300} showPointLabels />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <WidgetCard title="EEG realizados por tipo de lead (semanal)" explanation={explanations.eegDoneByType} bodyHeight={300} isLoading={recentDoneEegResult.isLoading} isError={recentDoneEegResult.isError}>
                            <TimeSeriesChart points={doneEegByTypePoints} series={LEAD_TYPE_SERIES_WITH_TOTAL} height={300} showPointLabels />
                        </WidgetCard>
                    </Grid>
                </Grid>

                <WidgetCard title="EEG marcados por clínica (semanal)" explanation={explanations.eegByClinic} bodyHeight={300} isLoading={recentBookedEegResult.isLoading} isError={recentBookedEegResult.isError}>
                    <TimeSeriesChart points={bookedEegByClinicPoints} series={EEG_BY_CLINIC_SERIES_WITH_TOTAL} height={300} showPointLabels />
                </WidgetCard>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <WidgetCard title="Avaliações realizadas por mês (por clínica)" explanation={explanations.evalMonthlyChart} bodyHeight={340} isLoading={evalDoneByClinicMonthlyResult.isLoading} isError={evalDoneByClinicMonthlyResult.isError}>
                            <StackedBarChart points={evalByClinicPoints} series={clinicChartSeries} categoryKey="monthLabel" height={340} showBarLabels showTotalLabel barDrillDown={evalByClinicBarDrill} />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" flexDirection="column" gap={3} height="100%">
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Avaliações por mês (média mensal)" value={evalDoneMonthlyResult.data ? evalMonthlyAverage ?? undefined : undefined} color={DASHBOARD_COLORS.done} explanation={explanations.evalPerMonth} isLoading={evalDoneMonthlyResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Avaliações feitas este mês" value={evalDoneThisMonthResult.data ? singleCount(evalDoneThisMonthResult.data) : undefined} color={DASHBOARD_COLORS.done} explanation={explanations.evalDoneThisMonth} isLoading={evalDoneThisMonthResult.isLoading} drillDown={drills.evalDoneMonth} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Avaliações marcadas este mês" value={evalBookedThisMonthResult.data ? singleCount(evalBookedThisMonthResult.data) : undefined} color={DASHBOARD_COLORS.done} explanation={explanations.evalBookedMonth} isLoading={evalBookedThisMonthResult.isLoading} drillDown={drills.evalBookedMonth} sx={{ width: "100%" }} />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <WidgetCard title="Vendas por mês (por clínica)" explanation={explanations.salesMonthlyChart} bodyHeight={340} isLoading={salesByClinicResult.isLoading} isError={salesByClinicResult.isError}>
                            <StackedBarChart points={salesByClinicPoints} series={clinicChartSeries} categoryKey="monthLabel" height={340} showBarLabels showTotalLabel barDrillDown={salesByClinicBarDrill} />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" flexDirection="column" gap={3} height="100%">
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Vendas por mês (média mensal)" value={salesMonthlyResult.data ? salesMonthlyAverage ?? undefined : undefined} color={DASHBOARD_COLORS.cancelled} explanation={explanations.salesPerMonth} isLoading={salesMonthlyResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Vendas este mês" value={salesThisMonthResult.data ? singleCount(salesThisMonthResult.data) : undefined} color={DASHBOARD_COLORS.cancelled} explanation={explanations.salesThisMonth} isLoading={salesThisMonthResult.isLoading} drillDown={drills.salesThisMonth} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Tratamentos a começar (até fim do mês)" value={treatmentsFirstSessionResult.data ? treatmentsUntilEndOfMonth : undefined} color={DASHBOARD_COLORS.cancelled} explanation={explanations.treatmentsUntilEndOfMonth} isLoading={treatmentsFirstSessionResult.isLoading} drillDown={drills.treatmentsUntilEndOfMonth} sx={{ width: "100%" }} />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <WidgetCard
                    title="Tratamentos iniciados por tipo (mensal)"
                    explanation={explanations.treatmentsByTypeChart}
                    bodyHeight={340}
                    isLoading={treatmentsFirstStartedResult.isLoading || treatmentTypeMixResult.isLoading}
                    isError={treatmentsFirstStartedResult.isError || treatmentTypeMixResult.isError}
                >
                    <TimeSeriesChart points={treatmentsByTypePoints} series={TREATMENT_TYPE_SERIES} height={340} showPointLabels />
                </WidgetCard>
            </DashboardSection>

            <DashboardSection title="Análises pela data de criação da lead">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <WidgetCard title="Resultados de marketing (todos)" explanation={explanations.mktResultsAll} bodyHeight={320} isLoading={leadsMonthlyResult.isLoading} isError={leadsMonthlyResult.isError}>
                            <TimeSeriesChart
                                points={mktResultsPoints}
                                series={[
                                    { key: "leads", label: "Leads", color: DASHBOARD_COLORS.highlight, rightAxis: true },
                                    { key: "bookedEvals", label: "1ªs avaliações marcadas", color: GAUGE_MIDDLE_COLOR },
                                    { key: "doneEvals", label: "Avaliações feitas", color: DASHBOARD_COLORS.done },
                                    { key: "sales", label: "Vendas", color: DASHBOARD_COLORS.cancelled },
                                ]}
                                height={320}
                            />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" flexDirection="column" gap={3} height="100%">
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Leads mensais (cohort 13→3m)" value={cohortLeadsResult.data ? cohortAverageLeads ?? undefined : undefined} color={DASHBOARD_COLORS.highlight} explanation={explanations.cohortAvgLeads} isLoading={cohortLeadsResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Avaliações mensais (cohort 13→3m)" value={cohortTreatmentsResult.data ? cohort.averageMonthlyEvaluations ?? undefined : undefined} color={DASHBOARD_COLORS.done} explanation={explanations.cohortAvgEvals} isLoading={cohortTreatmentsResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Vendas mensais (cohort 13→3m)" value={cohortTreatmentsResult.data ? cohort.averageMonthlySales ?? undefined : undefined} color={DASHBOARD_COLORS.cancelled} explanation={explanations.cohortAvgSales} isLoading={cohortTreatmentsResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <WidgetCard title="Eficiência de marketing (%)" explanation={explanations.marketingEfficiency} bodyHeight={320} isLoading={efficiencyTreatmentsResult.isLoading} isError={efficiencyTreatmentsResult.isError}>
                            <TimeSeriesChart
                                points={efficiencyPoints}
                                series={[
                                    { key: "leadsToBookings", label: "Leads → Marcações", color: GAUGE_MIDDLE_COLOR },
                                    { key: "leadsToEvaluations", label: "Leads → Avaliações", color: DASHBOARD_COLORS.done },
                                    { key: "evaluationsToClients", label: "Avaliações → Vendas", color: DASHBOARD_COLORS.cancelled },
                                    { key: "leadsToClients", label: "Leads → Vendas", color: DASHBOARD_COLORS.highlight },
                                ]}
                                height={320}
                            />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" flexDirection="column" gap={3} height="100%">
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Leads → Clientes (13→3m)" value={cohortTreatmentsResult.data ? (cohort.leadsToClientsPct === null ? undefined : `${cohort.leadsToClientsPct}%`) : undefined} color={DASHBOARD_COLORS.highlight} explanation={explanations.cohortLeadsToClients} isLoading={cohortTreatmentsResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Leads → Avaliações (13→3m)" value={cohortTreatmentsResult.data ? (cohort.leadsToEvaluationsPct === null ? undefined : `${cohort.leadsToEvaluationsPct}%`) : undefined} color={DASHBOARD_COLORS.done} explanation={explanations.cohortLeadsToEvals} isLoading={cohortTreatmentsResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Avaliações → Clientes (13→3m)" value={cohortTreatmentsResult.data ? (cohort.evaluationsToClientsPct === null ? undefined : `${cohort.evaluationsToClientsPct}%`) : undefined} color={DASHBOARD_COLORS.cancelled} explanation={explanations.cohortEvalsToClients} isLoading={cohortTreatmentsResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <WidgetCard title="Mkt Cost Per Goal — custo por objetivo (mensal)" explanation={explanations.mktCostPerGoal} bodyHeight={320} isLoading={spendMonthlyResult.isLoading || qualifiedLeadsMonthlyResult.isLoading} isError={spendMonthlyResult.isError || qualifiedLeadsMonthlyResult.isError}>
                            <ComboBarLineChart
                                points={mktCostGoalPoints}
                                bar={{ key: "spend", label: "Gasto FB + Google", color: DASHBOARD_COLORS.neutral, valueFormatter: formatEuros }}
                                lines={[
                                    { key: "costPerLead", label: "Custo por lead qualificada", color: DASHBOARD_COLORS.highlight, countKey: "qualifiedLeadsCount", countLabel: "leads qualif.", valueFormatter: formatEuros },
                                    { key: "costPerBooking", label: "Custo por booking", color: GAUGE_MIDDLE_COLOR, countKey: "bookingsCount", countLabel: "marcações", valueFormatter: formatEuros },
                                    { key: "costPerEvaluation", label: "Custo por avaliação", color: DASHBOARD_COLORS.done, countKey: "evaluationsCount", countLabel: "avaliações", countDetail: { key: "pendingEvaluationsCount", label: "por realizar" }, valueFormatter: formatEuros },
                                    { key: "costPerTreatment", label: "Custo por tratamento", color: DASHBOARD_COLORS.cancelled, countKey: "treatmentsCount", countLabel: "tratamentos", countDetail: { key: "openTreatmentsCount", label: "em aberto" }, valueFormatter: formatEuros },
                                ]}
                                height={320}
                                barValueFormatter={formatQuantity}
                            />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" flexDirection="column" gap={3} height="100%">
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Receita avaliações (média mensal)" value={revenueItemsResult.data ? formatEuros(revenueMonthlyAverage) : undefined} color={DASHBOARD_COLORS.done} explanation={explanations.revenueAvg} isLoading={revenueItemsResult.isLoading} sx={{ width: "100%" }} />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex" }}>
                                <StatCard label="Receita avaliações este mês" value={revenueItemsResult.data ? formatEuros(revenueThisMonth) : undefined} color={DASHBOARD_COLORS.done} explanation={explanations.revenueThisMonth} isLoading={revenueItemsResult.isLoading} drillDown={drills.revenueThisMonth} sx={{ width: "100%" }} />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <PivotTableCard
                    title="Eficiência dos grupos de anúncios"
                    explanation={explanations.adGroup}
                    pivot={adGroupPivot}
                    rowHeaderLabel="Fonte / Grupo de anúncios (FB) ou campanha"
                    cellDrillDown={adGroupCellDrill}
                    maxBodyHeight={520}
                    isLoading={adGroupLeadsResult.isLoading || adGroupSpendResult.isLoading || facebookAdsetNamesResult.isLoading}
                    isError={adGroupLeadsResult.isError || adGroupSpendResult.isError || facebookAdsetNamesResult.isError}
                    headerControls={
                        <DashboardDateFilter label="Período" filterKey="adGroupPeriod" values={adGroupFilterValues} onChange={setLocalFilter(setAdGroupFilterValues)} presets={AD_GROUP_DATE_PRESETS} />
                    }
                />

                <DashboardSection
                    title="Grupos de anúncios do Facebook"
                    subtitle={`Leads pela data de criação; só grupos de anúncios com mais de ${ADSET_STATUS_MIN_LEADS} leads. O período aplica-se aos dois gráficos.`}
                    headerControls={
                        <DashboardDateFilter
                            label="Período"
                            filterKey="adsetStatusPeriod"
                            values={adsetStatusFilterValues}
                            onChange={setLocalFilter(setAdsetStatusFilterValues)}
                            presets={AD_GROUP_DATE_PRESETS}
                        />
                    }
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <WidgetCard
                                title="Estado das leads"
                                explanation={explanations.adsetStatus}
                                bodyHeight={adsetChartsHeight}
                                isLoading={adsetStatusLeadsResult.isLoading || facebookAdsetNamesResult.isLoading}
                                isError={adsetStatusLeadsResult.isError || facebookAdsetNamesResult.isError}
                            >
                                <StackedBarChart
                                    points={adsetStatusPoints}
                                    series={ADSET_STATUS_SERIES}
                                    categoryKey="adset"
                                    height={adsetChartsHeight}
                                    horizontalBars
                                    showBarLabels
                                    showTotalLabel
                                    barDrillDown={adsetStatusBarDrill}
                                />
                            </WidgetCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <WidgetCard
                                title="Custos"
                                explanation={explanations.adsetCost}
                                bodyHeight={adsetChartsHeight}
                                isLoading={adsetStatusLeadsResult.isLoading || facebookAdsetNamesResult.isLoading || facebookSpendByAdsetResult.isLoading}
                                isError={adsetStatusLeadsResult.isError || facebookAdsetNamesResult.isError || facebookSpendByAdsetResult.isError}
                            >
                                <ComposedBarLineChart
                                    points={adsetCostChartPoints}
                                    barSeries={ADSET_COST_SERIES}
                                    lineSeries={[]}
                                    categoryKey="adset"
                                    height={adsetChartsHeight}
                                    valueFormatter={formatEuros}
                                    horizontalBars
                                    categorySubLabel={(point) => (Number(point.spend) > 0 ? `Gasto: ${formatEuros(Number(point.spend))}` : null)}
                                />
                            </WidgetCard>
                        </Grid>
                    </Grid>
                </DashboardSection>

                <WidgetCard title="Leads – Marcações – Custos (semanal)" explanation={explanations.leadsBookingsCosts} bodyHeight={320} isLoading={weeklyLeadsResult.isLoading || weeklySpendResult.isLoading} isError={weeklyLeadsResult.isError || weeklySpendResult.isError}>
                    <ComboBarLineChart
                        points={leadsBookingsCostsPoints}
                        bar={{ key: "mktCosts", label: "Custos mkt", color: DASHBOARD_COLORS.neutral }}
                        lines={[
                            { key: "leads", label: "Leads", color: DASHBOARD_COLORS.highlight },
                            { key: "bookings", label: "Marcações (1ª visita)", color: DASHBOARD_COLORS.done },
                            { key: "costPer1stVisit", label: "Custo por 1ª visita", color: DASHBOARD_COLORS.cancelled },
                        ]}
                        height={320}
                        barValueFormatter={formatQuantity}
                    />
                </WidgetCard>

                <WidgetCard title="Leads vs marcações por dia (últimos 15 dias)" explanation={explanations.leadsBookingsDaily} bodyHeight={300} isLoading={dailyLeadsResult.isLoading || dailyBookingsResult.isLoading} isError={dailyLeadsResult.isError || dailyBookingsResult.isError}>
                    <TimeSeriesChart
                        points={leadsBookingsPoints}
                        series={[
                            { key: "leads", label: "Leads", color: DASHBOARD_COLORS.highlight },
                            { key: "bookings", label: "Primeiras visitas marcadas", color: DASHBOARD_COLORS.done },
                        ]}
                        height={300}
                    />
                </WidgetCard>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <PivotTableCard
                            title="Vendas de tratamentos por grupo de perturbação"
                            explanation={explanations.disorderPivot}
                            pivot={disorderPivot}
                            rowHeaderLabel="Grupo de perturbação"
                            cellDrillDown={disorderCellDrill}
                            maxBodyHeight={320}
                            isLoading={disorderTreatmentsResult.isLoading}
                            isError={disorderTreatmentsResult.isError}
                            headerControls={
                                <DashboardDateFilter label="Período" filterKey="disorderPeriod" values={disorderFilterValues} onChange={setLocalFilter(setDisorderFilterValues)} />
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <PivotTableCard
                            title="Tratamentos iniciados por tipo de cliente"
                            explanation={explanations.clientTypePivot}
                            pivot={clientTypePivot}
                            rowHeaderLabel="Tipo de cliente"
                            cellDrillDown={clientTypeCellDrill}
                            maxBodyHeight={320}
                            isLoading={clientTypeTreatmentsResult.isLoading}
                            isError={clientTypeTreatmentsResult.isError}
                            headerControls={
                                <DashboardDateFilter label="Período" filterKey="clientTypePeriod" values={clientTypeFilterValues} onChange={setLocalFilter(setClientTypeFilterValues)} />
                            }
                        />
                    </Grid>
                </Grid>
            </DashboardSection>

            <DashboardSection
                title="Comparação entre crianças e adultos"
                subtitle="Todas as métricas pela data de criação da lead. O período abaixo aplica-se a toda a secção."
                headerControls={
                    <DashboardDateFilter
                        label="Período"
                        filterKey="segmentPeriod"
                        values={segmentFilterValues}
                        onChange={setLocalFilter(setSegmentFilterValues)}
                        presets={SEGMENT_DATE_PRESETS}
                    />
                }
            >
                <PivotTableCard
                    title="Funil por segmento"
                    explanation={explanations.segmentFunnel}
                    pivot={segmentPivot}
                    rowHeaderLabel="Tipo de cliente"
                    cellDrillDown={segmentCellDrill}
                    maxBodyHeight={320}
                    isLoading={segmentLeadsResult.isLoading || segmentSpendResult.isLoading}
                    isError={segmentLeadsResult.isError || segmentSpendResult.isError}
                />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <WidgetCard
                            title="Conversão por etapa (crianças vs adultos)"
                            explanation={explanations.segmentConversion}
                            bodyHeight={340}
                            isLoading={segmentLeadsResult.isLoading}
                            isError={segmentLeadsResult.isError}
                        >
                            <ComposedBarLineChart
                                points={segmentConversionPoints}
                                barSeries={SEGMENT_COMPARISON_SERIES}
                                lineSeries={[]}
                                categoryKey="stage"
                                height={340}
                                valueFormatter={(value) => formatPercent(value, 1)}
                                angledCategoryLabels
                            />
                        </WidgetCard>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <WidgetCard
                            title="Custo por etapa (crianças vs adultos)"
                            explanation={explanations.segmentCost}
                            bodyHeight={340}
                            isLoading={segmentLeadsResult.isLoading || segmentSpendResult.isLoading}
                            isError={segmentLeadsResult.isError || segmentSpendResult.isError}
                        >
                            <ComposedBarLineChart
                                points={segmentCostPoints}
                                barSeries={SEGMENT_COMPARISON_SERIES}
                                lineSeries={[]}
                                categoryKey="stage"
                                height={340}
                                valueFormatter={formatEuros}
                                angledCategoryLabels
                            />
                        </WidgetCard>
                    </Grid>
                </Grid>
            </DashboardSection>
        </DashboardPage>
    );
};
